/** Thrown when the server rejects a request; carries the HTTP status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    /** Human-readable explanation from the server, when it sent one. */
    readonly detail: string | null = null,
    /** Every reason, when the server refused a document for several (`problems`). */
    readonly problems: readonly string[] | null = null,
  ) {
    super(detail ?? `${code} (HTTP ${status})`);
    this.name = 'ApiError';
  }
}

/**
 * `fetch` wrapper that speaks the server's JSON error shape and turns an
 * expired session into a redirect back to the login page.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
      ...init.headers,
    },
  });

  // 204 (e.g. DELETE) has no body to parse.
  if (response.ok) return response.status === 204 ? (undefined as T) : ((await response.json()) as T);

  let code = `http_${response.status}`;
  let detail: string | null = null;
  let problems: string[] | null = null;
  try {
    const body = (await response.json()) as { error?: unknown; message?: unknown; problems?: unknown };
    if (typeof body.error === 'string') code = body.error;
    if (typeof body.message === 'string') detail = body.message;
    if (Array.isArray(body.problems)) problems = body.problems.filter((p): p is string => typeof p === 'string');
  } catch {
    // Non-JSON error body; keep the status-derived code.
  }
  throw new ApiError(response.status, code, detail, problems);
}

export async function login(password: string): Promise<void> {
  await api('/api/auth/login', { method: 'POST', body: JSON.stringify({ password }) });
}

export async function logout(): Promise<void> {
  await api('/api/auth/logout', { method: 'POST' });
}

/* ----------------------------------------------------------------- settings */

/**
 * Models an agent can be run on, mirroring the server's `AGENT_MODELS`.
 *
 * Claude Code's own `--model` aliases, so each keeps meaning the latest model
 * of its family. The empty string is the form the `<select>` uses for "no
 * choice" and is sent as `null`.
 */
export const AGENT_MODELS = ['opus', 'sonnet', 'haiku', 'fable'] as const;

export type AgentModel = (typeof AGENT_MODELS)[number];

/**
 * The levels `claude --effort` takes, mirroring the server's `EFFORT_LEVELS`.
 * `null` on a stage passes no flag and lets Claude Code choose.
 */
export const EFFORT_LEVELS = ['low', 'medium', 'high', 'xhigh', 'max'] as const;

export type EffortLevel = (typeof EFFORT_LEVELS)[number];

/** Mirrors the server's `AppSettings`. */
export interface Settings {
  /** Model the planning conversation runs on; `null` lets the CLI choose. */
  planningModel: AgentModel | null;
  /** Model each production step runs on; `null` lets the CLI choose. */
  productionModel: AgentModel | null;
  /** Model the review pass runs on; `null` lets the CLI choose. */
  reviewModel: AgentModel | null;
  /** Model the ideas worker drafts a batch on; `null` lets the CLI choose. */
  ideasModel: AgentModel | null;
  /** `--effort` per stage (synthesis O3); `null` lets the CLI choose. */
  planningEffort: EffortLevel | null;
  composeEffort: EffortLevel | null;
  productionEffort: EffortLevel | null;
  reviewEffort: EffortLevel | null;
  critiqueEffort: EffortLevel | null;
  ideasEffort: EffortLevel | null;
  /** The Meta ads agent's review; set in Settings → Meta Ads → Agent. */
  adsEffort: EffortLevel | null;
  maxConcurrentProductions: number;
  stepTimeoutMinutes: number;
  renderTimeoutMinutes: number;
  maxTurns: number;
  maxParallelSteps: number;
  containerMemoryGb: number;
  containerCpus: number;
  /** Spend cap a new content is pre-filled with, in US dollars. */
  defaultSpendCapUsd: number;
  /** 0 means no monthly ceiling on provider spend (US-059). */
  monthlySpendCapUsd: number;
  /** Where the sidebar starts warning, as a percentage of the monthly cap (US-061). */
  spendWarnThresholdPercent: number;
  /** What one `mc critique hooks` run may spend on the critic model (the ad bible US-024); 0 never duels. */
  hookDuelCapUsd: number;
  /** Render-probe thresholds (US-047): seconds, seconds, seconds and dBTP. */
  maxBlackSeconds: number;
  maxFrozenSeconds: number;
  maxSilentSeconds: number;
  truePeakCeilingDbtp: number;
  /** Automatic revision rounds the review pass may run (US-048); 0 turns them off. */
  reviewRevisionRounds: number;
  /**
   * How old a repository's brand profile may be before creating a content
   * extracts it again, in days (US-071).
   */
  brandRefreshDays: number;
  /** Who speaks a voiceover when the plan does not say (US-063). */
  ttsProvider: TtsProvider;
  /** The voice id on that provider; a plan may name another per step. */
  ttsVoice: string;
  /**
   * Settings → Defaults (US-089): what the new-content form starts filled in
   * with. Every one of them is editable per content and is copied onto the
   * content as it is created, so changing one here reaches the next form and
   * nothing that already exists.
   */
  defaultFormats: string[];
  defaultTone: string;
  defaultVideoSeconds: number;
  defaultCaptions: boolean;
  defaultMusic: boolean;
  defaultVoiceover: boolean;
  /** Stop a narrated content at its script, for the operator to read (US-090). */
  defaultApproveScript: boolean;
  /** Plan and produce without waiting for the operator (one-shot ads US-020). */
  defaultAutopilot: boolean;
  defaultWatermark: boolean;
  /**
   * The language a new content starts in when its product's profile names
   * none (synthesis C2): a BCP-47 tag such as `nl-NL`.
   */
  defaultLanguage: string;
  /**
   * Settings → Video generation → Image animation (US-002): the switch, the
   * OpenRouter image-to-video model, and the three caps an `animate` step is
   * held to.
   */
  animationEnabled: boolean;
  animationModel: string;
  maxAnimationsPerContent: number;
  maxAnimationSeconds: number;
  maxAnimationAttempts: number;
  /**
   * Settings → Video generation → Talking head: the switch, the model that
   * lip-syncs a photo to the narration, and the caps a `talk` step is held to.
   */
  talkEnabled: boolean;
  talkModel: string;
  maxTalksPerContent: number;
  maxTalkSeconds: number;
  maxTalkAttempts: number;
  /**
   * The shared library's embeddings (US-018): the OpenRouter model an item's
   * words are embedded with, and the last failed call, verbatim — written by
   * the server, never by the form.
   */
  libraryEmbeddingModel: string;
  libraryEmbeddingError: LibraryEmbeddingError | null;
  /** The OpenRouter vision model that writes library items' titles, descriptions and tags. */
  libraryDescriberModel: string;
}

/** The last embedding call that failed, as the server recorded it. */
export interface LibraryEmbeddingError {
  message: string;
  model: string;
  at: string;
}

/** Mirrors the server's `TTS_PROVIDERS`: free in the container, or paid. */
export type TtsProvider = 'kokoro' | 'elevenlabs';

/** Every field is optional: a `PUT` is partial and answers with the whole. */
export interface SettingsUpdate {
  /** `null` hands the choice back to Claude Code's own default. */
  planningModel?: AgentModel | null;
  productionModel?: AgentModel | null;
  reviewModel?: AgentModel | null;
  ideasModel?: AgentModel | null;
  planningEffort?: EffortLevel | null;
  composeEffort?: EffortLevel | null;
  productionEffort?: EffortLevel | null;
  reviewEffort?: EffortLevel | null;
  critiqueEffort?: EffortLevel | null;
  ideasEffort?: EffortLevel | null;
  maxConcurrentProductions?: number;
  stepTimeoutMinutes?: number;
  renderTimeoutMinutes?: number;
  maxTurns?: number;
  maxParallelSteps?: number;
  containerMemoryGb?: number;
  containerCpus?: number;
  defaultSpendCapUsd?: number;
  monthlySpendCapUsd?: number;
  spendWarnThresholdPercent?: number;
  hookDuelCapUsd?: number;
  maxBlackSeconds?: number;
  maxFrozenSeconds?: number;
  maxSilentSeconds?: number;
  truePeakCeilingDbtp?: number;
  reviewRevisionRounds?: number;
  brandRefreshDays?: number;
  ttsProvider?: TtsProvider;
  ttsVoice?: string;
  defaultFormats?: string[];
  defaultTone?: string;
  defaultVideoSeconds?: number;
  defaultCaptions?: boolean;
  defaultMusic?: boolean;
  defaultVoiceover?: boolean;
  defaultApproveScript?: boolean;
  defaultAutopilot?: boolean;
  defaultWatermark?: boolean;
  defaultLanguage?: string;
  animationEnabled?: boolean;
  animationModel?: string;
  maxAnimationsPerContent?: number;
  maxAnimationSeconds?: number;
  maxAnimationAttempts?: number;
  talkEnabled?: boolean;
  talkModel?: string;
  maxTalksPerContent?: number;
  maxTalkSeconds?: number;
  maxTalkAttempts?: number;
  libraryEmbeddingModel?: string;
  libraryDescriberModel?: string;
}

export async function fetchSettings(signal?: AbortSignal): Promise<Settings> {
  return api<Settings>('/api/settings', signal ? { signal } : {});
}

export async function saveSettings(update: SettingsUpdate): Promise<Settings> {
  return api<Settings>('/api/settings', { method: 'PUT', body: JSON.stringify(update) });
}

/* ------------------------------------------------------------------ storage */

/** Mirrors the server's `AddressingStyle`. */
export type AddressingStyle = 'virtual-hosted' | 'path';

/** Mirrors `StorageSettings`: the secret is write-only, its last four are not. */
export interface StorageSettings {
  endpoint: string | null;
  region: string;
  bucket: string;
  accessKeyId: string;
  secretLastFour: string | null;
  secretConfigured: boolean;
  keyPrefix: string;
  addressingStyle: AddressingStyle;
  publicBaseUrl: string | null;
  keepCopiesOnDelete: boolean;
  /** Bucket, key and secret all present: an upload can be attempted. */
  configured: boolean;
  lastTest: StorageTestSummary | null;
  /** The last nightly orphan scan; `null` until one has run. */
  orphans: StorageOrphanReport | null;
}

export interface StorageTestSummary {
  ok: boolean;
  at: string;
  message: string;
}

/**
 * Mirrors `StorageOrphanReport`: objects under the key prefix that no asset
 * row claims. The scan counts them and never removes them, which is what the
 * overview's wording has to keep saying.
 */
export interface StorageOrphanReport {
  ranAt: string;
  scanned: number;
  orphans: number;
  bytes: number;
  sample: string[];
  error: string | null;
}

/** Every field optional: a `PUT` is partial and answers with the whole. */
export interface StorageSettingsUpdate {
  endpoint?: string | null;
  region?: string;
  bucket?: string;
  accessKeyId?: string;
  /** A string replaces the stored secret; `null` clears it. */
  secretAccessKey?: string | null;
  keyPrefix?: string;
  addressingStyle?: AddressingStyle;
  publicBaseUrl?: string | null;
  keepCopiesOnDelete?: boolean;
}

/**
 * One of the five S3-compatible services the settings shape is known to work
 * against. It comes from the server rather than being retyped here, so the
 * panel can never describe a shape the server does not sign requests with.
 */
export interface StorageProvider {
  name: string;
  endpoint: string;
  region: string;
  addressingStyle: AddressingStyle;
  note: string;
}

/** The rule to paste into the bucket, with this install's own origin in it. */
export interface StorageCorsHelp {
  origin: string;
  json: string;
  help: string;
}

export interface StorageView {
  storage: StorageSettings;
  addressingStyles: AddressingStyle[];
  providers: StorageProvider[];
  cors: StorageCorsHelp;
}

export type StorageCheckName =
  | 'head-bucket'
  | 'put-object'
  | 'presigned-get'
  | 'cors-preflight'
  | 'delete-object';

export interface StorageCheckResult {
  name: StorageCheckName;
  label: string;
  status: 'passed' | 'failed' | 'skipped';
  detail: string;
  /** The SDK's own message, unchanged. */
  error: string | null;
  code: string | null;
  httpStatus: number | null;
}

export interface StorageTestResult {
  ok: boolean;
  ranAt: string;
  message: string;
  checks: StorageCheckResult[];
  probeKey: string;
  cors: string;
  corsOrigin: string;
  /** The settings as they read after the run, with `lastTest` filled in. */
  storage: StorageSettings;
}

export async function fetchStorage(signal?: AbortSignal): Promise<StorageView> {
  return api<StorageView>('/api/settings/storage', signal ? { signal } : {});
}

export async function saveStorage(update: StorageSettingsUpdate): Promise<StorageView> {
  return api<StorageView>('/api/settings/storage', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

/**
 * Runs the five checks against the saved settings. A bucket that answers badly
 * is a `200` with `ok: false` and the SDK's error on the check that failed —
 * only an unconfigured bucket is a `400`.
 */
export async function testStorage(): Promise<StorageTestResult> {
  return api<StorageTestResult>('/api/settings/storage/test', { method: 'POST' });
}

/* ----------------------------------------------------------------- meta ads */

/** Mirrors `MetaSettings`: the token is write-only, its last four are not. */
export interface MetaSettings {
  /** Always `act_<digits>`, or `` when unset. */
  adAccountId: string;
  pageId: string;
  instagramId: string | null;
  pixelId: string;
  tokenLastFour: string | null;
  tokenConfigured: boolean;
  /** Meta refused the stored token (code 190) on the last Graph API call. */
  tokenInvalid: boolean;
  /** Token, ad account, Page and Pixel all present. */
  configured: boolean;
  /** The Settings badge: Not tested, Connected, Token invalid or Check failed. */
  connectionStatus: MetaConnectionStatus;
  /** The last **Test connection**, `null` when none has run since the ids changed. */
  lastTest: { ok: boolean; at: string; message: string } | null;
  /** The ad account as Meta described it on the last successful test. */
  account: MetaAdAccountInfo | null;
  /** The Facebook Page's name from the last test that read it: the default DSA names. */
  pageName: string | null;
}

export type MetaConnectionStatus = 'not_tested' | 'connected' | 'token_invalid' | 'check_failed';

export interface MetaAdAccountInfo {
  name: string;
  /** ISO 4217, e.g. `EUR`. */
  currency: string;
  /** IANA, e.g. `Europe/Amsterdam`. */
  timezone: string;
}

/** Mirrors `MetaCheckResult`: one requirement, what was found, and what to do. */
export interface MetaCheckResult {
  name: string;
  label: string;
  /** `warning` is not met yet but does not fail the test. */
  status: 'passed' | 'failed' | 'warning' | 'skipped';
  detail: string;
  /** What to do about it; `null` when there is nothing to do. */
  fix: string | null;
  /** Meta's own message, unchanged. */
  error: string | null;
  code: number | null;
}

/** What answers `POST /api/ads/connection/test`. */
export interface MetaConnectionTestResult {
  ok: boolean;
  ranAt: string;
  message: string;
  checks: MetaCheckResult[];
  account: MetaAdAccountInfo | null;
  pageName: string | null;
  meta: MetaSettings;
}

export interface MetaSettingsView {
  meta: MetaSettings;
  /** An agent campaign exists, so the ad account, Page and Pixel cannot change. */
  idsLocked: boolean;
}

/**
 * Every field optional: a `PUT` is partial. An empty or omitted token keeps
 * the stored one — removing it is {@link removeMetaToken}, never a save.
 */
export interface MetaSettingsUpdate {
  accessToken?: string;
  /** `123` or `act_123`; the server stores `act_123`. */
  adAccountId?: string;
  pageId?: string;
  /** `null` clears it. */
  instagramId?: string | null;
  pixelId?: string;
}

export async function fetchMeta(signal?: AbortSignal): Promise<MetaSettingsView> {
  return api<MetaSettingsView>('/api/settings/meta', signal ? { signal } : {});
}

export async function saveMeta(update: MetaSettingsUpdate): Promise<MetaSettingsView> {
  return api<MetaSettingsView>('/api/settings/meta', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

export async function removeMetaToken(): Promise<MetaSettingsView> {
  return api<MetaSettingsView>('/api/settings/meta/token', { method: 'DELETE' });
}

export async function testMetaConnection(): Promise<MetaConnectionTestResult> {
  return api<MetaConnectionTestResult>('/api/ads/connection/test', { method: 'POST' });
}

/** Mirrors `AdsBudgetState` (meta-ads-agent US-007): the month against the ceiling, minor units. */
export interface AdsBudgetState {
  month: string;
  timezone: string;
  currency: string;
  today: string;
  ceilingMinor: number | null;
  spentMinor: number;
  remainingMinor: number | null;
  daysLeft: number;
  maxDailyBudgetMinor: number | null;
  dailyBudgetsMinor: number;
}

/** Mirrors `AdsPacingView` (US-007): the hourly pacing check, and whether the server can see spend. */
export interface AdsPacing {
  lastPacingAt: string | null;
  failingSince: string | null;
  /** Three hours without reaching Meta: its spend cap is the only guard. */
  metaUnreachable: boolean;
  budget: AdsBudgetState;
}

export async function fetchAdsPacing(signal?: AbortSignal): Promise<AdsPacing> {
  return api<AdsPacing>('/api/ads/pacing', signal === undefined ? {} : { signal });
}

/** Mirrors `AdsRun` (US-012), the fields the controls read. */
export interface AdsRun {
  id: string;
  trigger: 'schedule' | 'manual' | 'launch' | 'retry';
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
}

/** Mirrors `AdsDecision` (US-005), the fields the controls read. */
export interface AdsDecision {
  id: string;
  actor: 'agent' | 'system' | 'operator';
  kind: string;
  targetMetaId: string | null;
  targetName: string | null;
  outcome: string;
  outcomeDetail: string | null;
  createdAt: string;
}

/** Mirrors `AdsAgentControlView` (meta-ads-agent US-014): enable, disable and the kill switch. */
export interface AdsAgentControls {
  enabled: boolean;
  readiness: {
    ready: boolean;
    /** What stands between the operator and "Enable agent", each with what to do. */
    missing: { key: string; message: string }[];
    creativeCandidates: number;
  };
  activeRun: AdsRun | null;
  /** Campaigns "Pause all ads" paused, which enabling resumes. */
  pausedByOperator: number;
  /** Active campaigns whose last "Pause all ads" Meta refused; "Try again" retries them. */
  pauseFailures: { campaignMetaId: string; campaignName: string; decision: AdsDecision }[];
}

export async function fetchAdsAgentControls(signal?: AbortSignal): Promise<AdsAgentControls> {
  return api<AdsAgentControls>('/api/ads/agent', signal === undefined ? {} : { signal });
}

/** Enables the agent and starts a `launch` run (after resuming what "Pause all ads" paused). 409 `not_ready` / `run_active`. */
export async function enableAdsAgent(): Promise<AdsAgentControls & { run: AdsRun; resumed: AdsDecision[] }> {
  return api('/api/ads/agent/enable', { method: 'POST' });
}

/** Stops future reviews; the ads keep running. */
export async function disableAdsAgent(): Promise<AdsAgentControls> {
  return api<AdsAgentControls>('/api/ads/agent/disable', { method: 'POST' });
}

/** The kill switch: pauses every active agent campaign and disables the agent. Failures are in `pauseFailures`. */
export async function pauseAllAds(): Promise<AdsAgentControls & { decisions: AdsDecision[] }> {
  return api('/api/ads/agent/pause-all', { method: 'POST' });
}

/** "Run review now": a `manual` run; 409 `run_active` while one is active. */
export async function startAdsReview(): Promise<{ run: AdsRun }> {
  return api<{ run: AdsRun }>('/api/ads/runs', { method: 'POST' });
}

/** Mirrors `AdsOverviewSpan` (meta-ads-agent US-015): one span of days added up. Money is minor units. */
export interface AdsOverviewSpan {
  from: string;
  to: string;
  /** Includes the last 48 hours, whose conversions Meta still attributes. */
  incompleteAttribution: boolean;
  spendMinor: number;
  impressions: number;
  linkClicks: number;
  landingPageViews: number;
  bookedCalls: number;
  proxyEvents: number;
  video3sViews: number;
  /** A fraction (0.0123 is 1.23 %); `null` on no impressions. */
  linkCtr: number | null;
  costPerLandingPageViewMinor: number | null;
  costPerProxyEventMinor: number | null;
  costPerBookedCallMinor: number | null;
}

/** Mirrors `AdsOverviewNode`: a campaign, ad set or ad of the overview table, last 30 days. */
export interface AdsOverviewNode {
  metaId: string;
  kind: 'campaign' | 'adset' | 'ad';
  name: string;
  status: string | null;
  effectiveStatus: string | null;
  pausedBy: 'agent' | 'system_ceiling' | 'operator' | 'external' | null;
  liveSince: string | null;
  ageHours: number | null;
  dailyBudgetMinor: number | null;
  metrics: AdsOverviewSpan;
  frequency: number | null;
  thumbnail: { contentId: string; assetId: string; video: boolean } | null;
  children: AdsOverviewNode[];
}

/** Mirrors `AdsOverviewRun`: a review, as the summary panel shows it. */
export interface AdsOverviewRun {
  id: string;
  trigger: AdsRun['trigger'];
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  summary: string | null;
  observations: string[];
  error: string | null;
}

/** Mirrors `AdsOverview` (US-015): the Ads page at a glance, read from what is stored. */
export interface AdsOverview {
  connected: boolean;
  connectionStatus: MetaConnectionStatus;
  adAccountId: string;
  currency: string;
  timezone: string;
  pausedByCeiling: number;
  lastRun: AdsOverviewRun | null;
  latestSummary: AdsOverviewRun | null;
  nextReviewAt: string | null;
  retryAt: string | null;
  budget: AdsBudgetState & { projectedMinor: number };
  kpis: { last7d: AdsOverviewSpan; previous7d: AdsOverviewSpan; last30d: AdsOverviewSpan };
  pacing: Omit<AdsPacing, 'budget'>;
  targetCostMinor: number | null;
  proxyEventLabel: string | null;
  bookedCallLabel: string;
  campaigns: AdsOverviewNode[];
}

export async function fetchAdsOverview(signal?: AbortSignal): Promise<AdsOverview> {
  return api<AdsOverview>('/api/ads/overview', signal === undefined ? {} : { signal });
}

export const ADS_DECISION_ACTORS = ['agent', 'system', 'operator'] as const;
export type AdsDecisionActor = (typeof ADS_DECISION_ACTORS)[number];
export const ADS_DECISION_OUTCOMES = ['executed', 'refused', 'failed', 'held'] as const;
export type AdsDecisionOutcome = (typeof ADS_DECISION_OUTCOMES)[number];

/** Mirrors `AdsDecisionView` (US-016): one decision, labelled, its JSON parsed. */
export interface AdsDecisionEntry {
  id: string;
  runId: string | null;
  actor: AdsDecisionActor;
  kind: string;
  label: string;
  targetMetaId: string | null;
  targetName: string | null;
  outcome: AdsDecisionOutcome;
  reason: string | null;
  ruleId: string | null;
  ruleDescription: string | null;
  judgeAfter: string | null;
  params: unknown;
  evidence: unknown;
  outcomeDetail: unknown;
  createdAt: string;
}

/** Mirrors `AdsDecisionLog` (US-016): one page of the decision log. */
export interface AdsDecisionLog {
  decisions: AdsDecisionEntry[];
  page: number;
  pageSize: number;
  total: number;
  kinds: { kind: string; label: string }[];
  targets: { metaId: string; name: string | null }[];
}

export interface AdsDecisionQuery {
  page?: number;
  actor?: AdsDecisionActor;
  outcome?: AdsDecisionOutcome;
  kind?: string;
  target?: string;
}

export async function fetchAdsDecisions(query: AdsDecisionQuery, signal?: AbortSignal): Promise<AdsDecisionLog> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return api<AdsDecisionLog>(
    `/api/ads/decisions${search === '' ? '' : `?${search}`}`,
    signal === undefined ? {} : { signal },
  );
}

/** Mirrors `AdsMetricValues` (`server/src/ads/metrics.ts`): every row of an ad's metrics table. Money is minor units, rates fractions. */
export interface AdsMetricValues {
  spendMinor: number;
  impressions: number;
  reach: number | null;
  frequency: number | null;
  cpmMinor: number | null;
  linkClicks: number;
  linkCtr: number | null;
  costPerLinkClickMinor: number | null;
  landingPageViews: number;
  costPerLandingPageViewMinor: number | null;
  proxyEvents: number;
  costPerProxyEventMinor: number | null;
  bookedCalls: number;
  costPerBookedCallMinor: number | null;
  video3sViews: number;
  hookRate: number | null;
  thruplays: number;
}

export type AdsMetricKey = keyof AdsMetricValues;
export const AD_DETAIL_WINDOWS = ['last_7d', 'previous_7d', 'last_30d', 'lifetime'] as const;
export type AdDetailWindowName = (typeof AD_DETAIL_WINDOWS)[number];

/** Mirrors `AdDetailWindow`: one window of one ad, against the pooled average of the other ads of its ad set. */
export interface AdDetailWindow {
  from: string;
  to: string;
  incompleteAttribution: boolean;
  values: AdsMetricValues;
  otherAds: AdsMetricValues | null;
  /** `values ÷ otherAds` per metric; `null` when the ad set has no other ad. */
  vsOtherAds: Record<AdsMetricKey, number | null> | null;
}

/** Mirrors `AdDetail` (meta-ads-agent US-022): one ad in full, read from what is stored. */
export interface AdDetail {
  ad: {
    metaId: string;
    name: string;
    status: string | null;
    effectiveStatus: string | null;
    pausedBy: AdsOverviewNode['pausedBy'];
    reviewFeedback: string[];
    createdAt: string;
    liveSince: string | null;
    ageHours: number | null;
  };
  adSet: { metaId: string; name: string } | null;
  campaign: { metaId: string; name: string } | null;
  adAccountId: string;
  currency: string;
  timezone: string;
  milestones: {
    ageReachedOn: string | null;
    testSpendMinor: number | null;
    lifetimeSpendMinor: number;
    fairTestOn: string | null;
    retirementCheckOn: string | null;
  };
  copy: {
    concept: string;
    versionId: string;
    primaryTexts: string[];
    headlines: string[];
    descriptions: string[];
    cta: string;
    landingUrl: string;
    urlTags: string;
    url: string;
  } | null;
  creative: {
    contentId: string;
    contentName: string;
    versionNumber: number;
    kind: 'image' | 'video';
    render: VersionFile | null;
    poster: VersionFile | null;
  } | null;
  proxyEventLabel: string | null;
  bookedCallLabel: string;
  otherAdCount: number;
  metrics: Record<AdDetailWindowName, AdDetailWindow>;
  daily: {
    date: string;
    spendMinor: number;
    impressions: number;
    linkClicks: number;
    landingPageViews: number;
    proxyEvents: number;
    bookedCalls: number;
    incompleteAttribution: boolean;
  }[];
  decisions: AdsDecisionEntry[];
}

export async function fetchAdDetail(metaId: string, signal?: AbortSignal): Promise<AdDetail> {
  return api<AdDetail>(`/api/ads/ads/${encodeURIComponent(metaId)}`, signal === undefined ? {} : { signal });
}

/** Mirrors `AdsRunSummary` (meta-ads-agent US-017): one row of the Reviews tab. */
export interface AdsRunSummary {
  id: string;
  trigger: AdsRun['trigger'];
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'held_usage_limit';
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  /** `null` while it runs or when it never started. */
  durationMs: number | null;
  model: string | null;
  effort: string | null;
  decisions: Record<AdsDecisionOutcome, number>;
  error: string | null;
}

/** Mirrors `AdsCreativeRequestView` (US-017, US-019). */
export interface AdsCreativeRequest {
  id: string;
  runId: string | null;
  concept: string;
  angle: string | null;
  format: string | null;
  aspectRatios: string[];
  hook: string | null;
  /** The agent's "why". */
  notes: string | null;
  status: 'open' | 'started' | 'dismissed';
  contentId: string | null;
  /** The started content's name; `null` before it starts or once the content is gone. */
  contentName: string | null;
  createdAt: string;
}

export type AdsRunCreativeRequest = AdsCreativeRequest;

/** Mirrors `AdsRunDetail` (US-017): one review in full. */
export interface AdsRunDetail extends AdsRunSummary {
  summary: string | null;
  observations: string[];
  /** Oldest first. */
  decisionList: AdsDecisionEntry[];
  creativeRequests: AdsRunCreativeRequest[];
  /** Pretty-printed JSON, or the stored text when it does not parse. */
  snapshot: string | null;
  rawOutput: string | null;
}

export async function fetchAdsRuns(signal?: AbortSignal): Promise<{ runs: AdsRunSummary[]; active: AdsRun | null }> {
  return api('/api/ads/runs', signal === undefined ? {} : { signal });
}

export async function fetchAdsRun(id: string, signal?: AbortSignal): Promise<AdsRunDetail> {
  return api<AdsRunDetail>(`/api/ads/runs/${encodeURIComponent(id)}`, signal === undefined ? {} : { signal });
}

/** Mirrors `AdsNoteView` (meta-ads-agent US-018): a note for the agent, waiting or read by a review. */
export interface AdsNote {
  id: string;
  body: string;
  createdAt: string;
  /** `null` while the note waits for the next review. */
  consumedBy: { id: string; status: AdsRun['status']; startedAt: string | null } | null;
}

/** Mirrors `MAX_NOTE_CHARS` (server `ads/snapshot.ts`). */
export const MAX_ADS_NOTE_CHARS = 2000;

export async function fetchAdsNotes(signal?: AbortSignal): Promise<{ notes: AdsNote[] }> {
  return api('/api/ads/notes', signal === undefined ? {} : { signal });
}

export async function saveAdsNote(body: string): Promise<{ note: AdsNote }> {
  return api('/api/ads/notes', { method: 'POST', body: JSON.stringify({ body }) });
}

export async function deleteAdsNote(id: string): Promise<void> {
  await api<void>(`/api/ads/notes/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Open requests, then started ones; dismissed ones are not listed (US-019). */
export async function fetchAdsCreativeRequests(signal?: AbortSignal): Promise<{ requests: AdsCreativeRequest[] }> {
  return api('/api/ads/creative-requests', signal === undefined ? {} : { signal });
}

export async function fetchAdsCreativeRequest(id: string, signal?: AbortSignal): Promise<{ request: AdsCreativeRequest }> {
  return api(`/api/ads/creative-requests/${encodeURIComponent(id)}`, signal === undefined ? {} : { signal });
}

/** The request is `started` with the content made from it; 409 `request_not_open` when it is not open. */
export async function startAdsCreativeRequest(id: string, contentId: string): Promise<{ request: AdsCreativeRequest }> {
  return api(`/api/ads/creative-requests/${encodeURIComponent(id)}/start`, {
    method: 'POST',
    body: JSON.stringify({ contentId }),
  });
}

export async function dismissAdsCreativeRequest(id: string): Promise<{ request: AdsCreativeRequest }> {
  return api(`/api/ads/creative-requests/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}

/** Mirrors `AdsSyncStatusView` (meta-ads-agent US-006): whether Refresh may run. */
export interface AdsSyncStatus {
  lastSyncAt: string | null;
  /** `null` when Refresh may run now. */
  nextAllowedAt: string | null;
  running: boolean;
}

/** Mirrors `AdsSyncView`. */
export interface AdsSyncOutcome extends AdsSyncStatus {
  result: { syncedAt: string; objects: number; days: number; windows: number; externalChanges: unknown[]; today: string };
}

export async function fetchAdsSyncStatus(signal?: AbortSignal): Promise<AdsSyncStatus> {
  return api<AdsSyncStatus>('/api/ads/sync', signal === undefined ? {} : { signal });
}

/** Refresh: fetches the agent's objects and insights from Meta; 429 `sync_too_soon` within 10 minutes of the last sync. */
export async function refreshAds(): Promise<AdsSyncOutcome> {
  return api<AdsSyncOutcome>('/api/ads/sync', { method: 'POST' });
}

/** Mirrors `MetaConversionEvent`: a Pixel standard event, or a custom conversion by id. */
export type MetaConversionEvent = { kind: 'standard'; name: string } | { kind: 'custom'; id: string };

/** Mirrors `AdsAgentSettings` (meta-ads-agent US-004). Money is minor units of `currency`. */
export interface AdsAgentSettings {
  currency: string;
  /** No passing connection test has read the account's currency yet. */
  currencyAssumed: boolean;
  ceilingMinor: number | null;
  targetCostMinor: number | null;
  bookedCallEvent: MetaConversionEvent;
  proxyEvent: MetaConversionEvent | null;
  landingUrl: string | null;
  countries: string[];
  /** The saved language, or the product brand book's. */
  language: string;
  languageSaved: string | null;
  creativeEnhancements: boolean;
  productRepositoryId: string | null;
  dsaRequired: boolean;
  dsaBeneficiary: string | null;
  dsaPayer: string | null;
  dsaBeneficiarySaved: string | null;
  dsaPayerSaved: string | null;
  model: AgentModel | null;
  effort: EffortLevel | null;
  budget: { dailyMinor: number; bookedCallsPerMonth: number; optimisesForProxy: boolean } | null;
  proxyRequired: boolean;
  missing: string[];
}

/** What answers `GET/PUT /api/settings/ads`. */
export interface AdsAgentSettingsView {
  agent: AdsAgentSettings;
  liveCampaigns: boolean;
  spentThisMonthMinor: number | null;
  pendingAdsetChanges: { fields: string[]; since: string } | null;
  countryOptions: { code: string; name: string }[];
  standardEvents: string[];
}

/** A partial save: amounts in display units, `null` clears to the default. */
export interface AdsAgentSettingsUpdate {
  ceiling?: string | null;
  targetCost?: string | null;
  bookedCallEvent?: MetaConversionEvent | null;
  proxyEvent?: MetaConversionEvent | null;
  landingUrl?: string | null;
  countries?: string[];
  language?: string | null;
  creativeEnhancements?: boolean;
  productRepositoryId?: string | null;
  dsaBeneficiary?: string | null;
  dsaPayer?: string | null;
  model?: AgentModel | null;
  effort?: EffortLevel | null;
}

export async function fetchAdsAgentSettings(signal?: AbortSignal): Promise<AdsAgentSettingsView> {
  return api<AdsAgentSettingsView>('/api/settings/ads', signal ? { signal } : {});
}

export async function saveAdsAgentSettings(update: AdsAgentSettingsUpdate): Promise<AdsAgentSettingsView> {
  return api<AdsAgentSettingsView>('/api/settings/ads', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

/* ------------------------------------------------------------------- claude */

/** Mirrors the server's `ClaudeAuthStatus`. */
export interface ClaudeAuthStatus {
  authenticated: boolean;
  authMethod: string | null;
  account: string | null;
  organization: string | null;
  subscription: string | null;
  checkedAt: string;
  /** Why the check could not run; `authenticated` is then always false. */
  error: string | null;
}

/** The temporary `claude auth login` container and its terminal, if running. */
export interface ClaudeLogin {
  active: boolean;
  terminalId: string | null;
  containerId: string | null;
  containerName: string;
}

export interface ClaudeState {
  status: ClaudeAuthStatus;
  login: ClaudeLogin;
}

/**
 * `refresh` skips the server's cached probe result — worth paying the extra
 * container start for right after a login, not on every page load.
 */
export async function fetchClaudeState(
  options: { refresh?: boolean; signal?: AbortSignal } = {},
): Promise<ClaudeState> {
  const path = options.refresh === true ? '/api/claude?refresh=1' : '/api/claude';
  return api<ClaudeState>(path, options.signal ? { signal: options.signal } : {});
}

/** Spawns the login container and opens the terminal running the login flow. */
export async function startClaudeLogin(): Promise<ClaudeState> {
  return api<ClaudeState>('/api/claude/login', { method: 'POST' });
}

/** Closes the login terminal, removes the container, and re-checks the status. */
export async function stopClaudeLogin(): Promise<ClaudeState> {
  return api<ClaudeState>('/api/claude/login', { method: 'DELETE' });
}

/* ------------------------------------------------------------- repositories */

/** Mirrors the server's `RepositoryView`: the private key is never included. */
export interface Repository {
  id: string;
  name: string;
  sshUrl: string;
  /** A label only; `null` for a URL no `owner/repo` could be read out of. */
  githubSlug: string | null;
  defaultBaseBranch: string;
  /** The deploy key line to paste into the forge; null for imported PEM keys. */
  publicKey: string | null;
  keyFingerprint: string | null;
  keySource: 'generated' | 'imported' | null;
  keyConfigured: boolean;
  /** Markdown folded into this repository's planning and production prompts. */
  marketingContext: string | null;
  /** The brand book extracted from this repository, or null while it has none. */
  brandBookId: string | null;
  brandStatus: 'none' | 'extracting' | 'ready' | 'failed';
  brandExtractedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface RepositoryInput {
  name?: string;
  sshUrl?: string;
  /** Omit to let the server derive it from the SSH URL. */
  githubSlug?: string | null;
  defaultBaseBranch?: string;
  /** Omit on create to have the server generate an ed25519 keypair. */
  privateKey?: string;
}

export interface ConnectionTestResult {
  ok: boolean;
  message: string;
  stdout: string;
  stderr: string;
}

/**
 * The list, and the server's own sentence about what the deploy key needs.
 *
 * The help text travels with the data it describes so the page cannot promise
 * something the server does not do; render `help` rather than retyping it.
 */
export interface RepositoryList {
  repositories: Repository[];
  help: string;
}

export async function fetchRepositoryList(signal?: AbortSignal): Promise<RepositoryList> {
  return api<RepositoryList>('/api/repositories', signal ? { signal } : {});
}

export async function fetchRepositories(signal?: AbortSignal): Promise<Repository[]> {
  return (await fetchRepositoryList(signal)).repositories;
}

export async function createRepository(input: RepositoryInput): Promise<Repository> {
  return api<Repository>('/api/repositories', { method: 'POST', body: JSON.stringify(input) });
}

export async function updateRepository(id: string, input: RepositoryInput): Promise<Repository> {
  return api<Repository>(`/api/repositories/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export async function deleteRepository(id: string): Promise<void> {
  await api<void>(`/api/repositories/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Runs `git ls-remote` in a runner container; a failed remote still resolves. */
export async function testRepositoryConnection(id: string): Promise<ConnectionTestResult> {
  return api<ConnectionTestResult>(`/api/repositories/${encodeURIComponent(id)}/test`, {
    method: 'POST',
  });
}

/* ---------------------------------------------------------------- the brand */

/** One palette entry, as the extraction found it in the repository. */
export interface BrandColour {
  name?: string;
  hex?: string;
  role?: string;
}

export interface BrandProduct {
  name?: string;
  tagline?: string;
  oneLiner?: string;
  claims?: string[];
  ctas?: string[];
  urls?: string[];
}

export interface BrandVisual {
  background?: string;
  surface?: string;
  text?: string;
  accent?: string;
  palette?: BrandColour[];
  gradients?: string[];
  displayFont?: string;
  bodyFont?: string;
  monoFont?: string;
  fontSources?: string[];
  logoPaths?: string[];
  iconPaths?: string[];
  radius?: string;
  darkMode?: boolean;
}

export interface BrandVoice {
  tone?: string;
  register?: string;
  bannedPhrases?: string[];
  sampleLines?: string[];
}

export interface BrandMoment {
  title?: string;
  sourcePaths?: string[];
  whatToShow?: string;
}

export interface BrandScreen {
  route?: string;
  sourcePath?: string;
  description?: string;
}

/** Mirrors the server's `BrandProfile` (PLAN §11): every field but one optional. */
export interface BrandProfile {
  product?: BrandProduct;
  visual?: BrandVisual;
  voice?: BrandVoice;
  product_moments?: BrandMoment[];
  screens?: BrandScreen[];
  extractedAt: string;
  sources?: string[];
}

/**
 * Mirrors the server's `BrandOverrides`: a partial profile the operator typed,
 * stored beside the extraction rather than merged into it.
 */
export type BrandOverrides = Partial<Omit<BrandProfile, 'extractedAt'>>;

/** Mirrors the server's `BrandOverrideKind`. */
export type BrandOverrideKind = 'text' | 'colour' | 'lines';

/**
 * Mirrors the server's `BrandOverrideField`. The list travels with the view
 * for the same reason `reads` does: the page must not offer a field the API
 * would refuse, so the server says which ones it edits.
 */
export interface BrandOverrideField {
  /** Dotted path into the profile, e.g. `product.tagline`. */
  path: string;
  label: string;
  kind: BrandOverrideKind;
  section: 'Product' | 'Palette' | 'Type' | 'Tone';
}

/** Mirrors the server's `RepositoryBrandStatus`. */
export type BrandStatus = 'none' | 'extracting' | 'ready' | 'failed';

/**
 * Mirrors the server's `BrandView`. `reads` and `help` travel with the profile
 * for the same reason the deploy-key help travels with the list: they describe
 * what the extraction container actually does, so the empty state cannot
 * promise something `mc-brand` does not look at.
 */
export interface BrandView {
  brandBookId: string;
  brandBookName: string;
  /** Where the brand comes from: a website's sources are links, a manual book is edited whole. */
  sourceType: BrandBookSourceType;
  /** The site a website book is read from; null for any other book. */
  websiteUrl: string | null;
  /** The pages the last successful website snapshot read; null until there is one. */
  snapshot: BrandBookSnapshot | null;
  /** The repository the book is extracted from; null for any other book, or once it is deleted. */
  repositoryId: string | null;
  repositoryName: string | null;
  status: BrandStatus;
  /** The extraction with the operator's corrections on top; what every plan reads. */
  profile: BrandProfile | null;
  /** The extraction on its own, so a corrected field can show what was found. */
  extracted: BrandProfile | null;
  overrides: BrandOverrides;
  overriddenFields: number;
  overrideFields: BrandOverrideField[];
  overridesHelp: string;
  markdown: string | null;
  extractedAt: string | null;
  error: string | null;
  /** A refresh of this brand book is running on the server right now. */
  refreshing: boolean;
  /** False when this server cannot start containers; **Refresh** is disabled. */
  available: boolean;
  /** The book's free-text marketing context, or null. */
  marketingContext: string | null;
  reads: string[];
  help: string;
}

/** Mirrors the server's `BrandBookSnapshot`: what a website snapshot read. */
export interface BrandBookSnapshot {
  url: string;
  takenAt: string;
  pages: { url: string; title: string }[];
}

/** Mirrors the server's `BrandRefreshResult`. */
export interface BrandRefreshResult {
  status: BrandStatus;
  message: string;
  profile: BrandProfile | null;
  markdown: string | null;
  extractedAt: string | null;
  error: string | null;
  s3Key: string | null;
  /** True when this call joined a refresh that was already running. */
  coalesced: boolean;
}

export async function fetchBrandBookBrand(
  brandBookId: string,
  signal?: AbortSignal,
): Promise<BrandView> {
  return api<BrandView>(
    `/api/brand-books/${encodeURIComponent(brandBookId)}/brand`,
    signal ? { signal } : {},
  );
}

/**
 * Runs the extraction and resolves with what it found. It is one container
 * over a shallow clone, so this request is slow by nature — and a second one
 * while it runs joins the first rather than starting another.
 */
export async function refreshBrandBookBrand(brandBookId: string): Promise<BrandRefreshResult> {
  return api<BrandRefreshResult>(
    `/api/brand-books/${encodeURIComponent(brandBookId)}/brand/refresh`,
    { method: 'POST' },
  );
}

/**
 * Stores a partial profile over the extraction and answers the whole brand
 * view as it now reads, merged.
 *
 * Partial the way every other `PUT` in this app is: a field that is not sent
 * keeps whatever correction it already had, and `null` removes the correction
 * so the field falls back to what the extraction found.
 */
export async function saveBrandBookOverrides(
  brandBookId: string,
  patch: BrandOverrides,
): Promise<BrandView> {
  return api<BrandView>(`/api/brand-books/${encodeURIComponent(brandBookId)}/brand/overrides`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
}

/** The server's cap on a marketing context, counted after trimming. */
export const MAX_MARKETING_CONTEXT_LENGTH = 10_000;

/** Replaces a brand book's free-text marketing context; null or '' removes it. */
export async function saveBrandBookContext(
  brandBookId: string,
  marketingContext: string | null,
): Promise<{ brandBookId: string; marketingContext: string | null }> {
  return api(`/api/brand-books/${encodeURIComponent(brandBookId)}/context`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ marketingContext }),
  });
}

/* ---------------------------------------------------------- audience */

/** An audience of the active guidelines version, as its `audience.json` describes it. */
export interface AudienceOption {
  key: string;
  name: string;
  language: string;
  description: string | null;
}

/**
 * The audience bible a brand book names (the ad bible US-010). `automatic` is
 * true when the server matched it from the product profile; the operator's
 * own choice, an audience or none, is never overruled by a match.
 */
export interface BrandBookAudience {
  brandBookId: string;
  audienceKey: string | null;
  automatic: boolean;
  /** The active version's audiences; empty while the guidelines store has none. */
  audiences: AudienceOption[];
}

export async function fetchBrandBookAudience(
  brandBookId: string,
  signal?: AbortSignal,
): Promise<BrandBookAudience> {
  return api<BrandBookAudience>(
    `/api/brand-books/${encodeURIComponent(brandBookId)}/audience`,
    signal ? { signal } : {},
  );
}

/** One made ad in a brand book's concept ledger (the ad bible US-018). */
export interface ConceptLedgerEntry {
  readonly contentId: string;
  readonly contentName: string;
  readonly dealtAt: string;
  readonly cards: readonly {
    readonly slot: string | null;
    readonly deck: string;
    readonly id: string;
    readonly name: string | null;
  }[];
  readonly frame0: string | null;
}

/** The made hands of a brand book, newest first. */
export async function fetchConceptLedger(
  brandBookId: string,
  signal?: AbortSignal,
): Promise<readonly ConceptLedgerEntry[]> {
  const body = await api<{ readonly entries: readonly ConceptLedgerEntry[] }>(
    `/api/brand-books/${encodeURIComponent(brandBookId)}/concept-ledger`,
    signal ? { signal } : {},
  );
  return body.entries;
}

/** Names the book's audience; `null` is "None", chosen. */
export async function saveBrandBookAudience(
  brandBookId: string,
  audienceKey: string | null,
): Promise<BrandBookAudience> {
  return api(`/api/brand-books/${encodeURIComponent(brandBookId)}/audience`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ audienceKey }),
  });
}

/* ---------------------------------------------------------- product profile */

/**
 * Mirrors the server's `ProductProfile` (synthesis C7): the facts about a
 * product the planner and the voice would otherwise ask for on every content.
 * Every field is optional.
 */
export interface ProductProfile {
  language?: string;
  address?: ContentAddress;
  website?: string;
  /** The one way the product is introduced: "Leo, de AI junior boekhouder". */
  descriptor?: string;
  cta?: { label?: string; url?: string };
  buyer?: string;
  statusQuo?: string;
  buyerWords?: string[];
  competitors?: ProfileCompetitor[];
  objections?: { objection: string; answer?: string }[];
  proof?: {
    claim: string;
    source: string;
    scope?: string;
    relatedParty?: boolean;
    permission?: string;
    guarantee?: boolean;
  }[];
  noGos?: string[];
  raiseToFail?: string[];
  allowLines?: string[];
  pronunciations?: { display: string; spoken: string; note?: string }[];
  voice?: { provider?: TtsProvider; voiceId?: string; model?: string; name?: string };
  cast?: ProfileCastMember[];
  rejectedVoices?: { voiceId: string; reason?: string }[];
  /** Tagged lines; a plain string is one from before lines had a source. */
  approvedLines?: (string | ProfileApprovedLine)[];
  removedApprovedLines?: ProfileRemovedLine[];
  rejectedLines?: string[];
  notes?: string;
}

/** A competitor in their own words (US-015). Mirrors the server's `ProfileCompetitor`. */
export interface ProfileCompetitor {
  name: string;
  claims: { text: string; url: string; fetched?: string }[];
}

/** An approved line and where it comes from (US-015). Mirrors the server's `ProfileApprovedLine`. */
export interface ProfileApprovedLine {
  text: string;
  source: 'operator' | 'approved-video';
  contentId: string | null;
  at: string;
}

/** An old approved line the migration set aside (US-015). Mirrors the server's `ProfileRemovedLine`. */
export interface ProfileRemovedLine {
  text: string;
  removedAt: string;
}

/** One character's voice on the brand book's cast (US-015). Mirrors the server's `ProfileCastMember`. */
/** A cast character's gender: `x` lets either voice speak them. Mirrors the server's `CastGender`. */
export type CastGender = 'f' | 'm' | 'x';

export interface ProfileCastMember {
  character: string;
  gender?: CastGender;
  /** `library:<item id>`, or `context:<id>` of an image uploaded to the brand book's context. */
  reference?: string;
  /** What they look like, in one line. */
  look?: string;
  provider?: TtsProvider;
  /** Absent for a character with a face and no voice yet. */
  voiceId?: string;
  model?: string;
  name?: string;
  language?: string;
  accent?: string;
}

/** Where a brand book's brand comes from. Mirrors the server's `BrandBookSourceType`. */
export type BrandBookSourceType = 'repository' | 'website' | 'manual';

/** One row of `GET /api/brand-books`. Mirrors the server's `BrandBookSummary`. */
export interface BrandBookSummary {
  id: string;
  name: string;
  sourceType: BrandBookSourceType;
  repositoryId: string | null;
  /** `null` for a book with no repository, or whose repository was deleted. */
  repositoryName: string | null;
  websiteUrl: string | null;
  brandStatus: BrandStatus;
  /** Why the last extraction failed; `null` unless `brandStatus` is `failed`. */
  brandError: string | null;
  brandExtractedAt: string | null;
  contextItemCount: number;
  /** How many contents are made in this book; a book in use cannot be deleted. */
  contentCount: number;
  createdAt: string;
  updatedAt: string;
}

/** What `POST /api/brand-books` takes: a name and one source. */
export type BrandBookInput =
  | { name: string; sourceType: 'repository'; repositoryId: string }
  | { name: string; sourceType: 'website'; websiteUrl: string }
  | { name: string; sourceType: 'manual' };

export async function fetchBrandBooks(signal?: AbortSignal): Promise<BrandBookSummary[]> {
  const { brandBooks } = await api<{ brandBooks: BrandBookSummary[] }>('/api/brand-books', { signal });
  return brandBooks;
}

export async function fetchBrandBook(id: string, signal?: AbortSignal): Promise<BrandBookSummary> {
  return api<BrandBookSummary>(`/api/brand-books/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export async function createBrandBook(input: BrandBookInput): Promise<BrandBookSummary> {
  return api<BrandBookSummary>('/api/brand-books', { method: 'POST', body: JSON.stringify(input) });
}

export async function renameBrandBook(id: string, name: string): Promise<BrandBookSummary> {
  return api<BrandBookSummary>(`/api/brand-books/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ name }),
  });
}

/** Refused with 409 `in_use` while any content is made in the book. */
export async function deleteBrandBook(id: string): Promise<void> {
  await api(`/api/brand-books/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** One brand book's profile, as `GET /api/brand-books/:id/profile` answers it. */
export interface BrandBookProfileView {
  brandBookId: string;
  brandBookName: string;
  repositoryId: string | null;
  repositoryName: string | null;
  /** `{}` until one is saved. */
  profile: ProductProfile;
  updatedAt: string;
}

export async function fetchBrandBookProfile(
  brandBookId: string,
  signal?: AbortSignal,
): Promise<BrandBookProfileView> {
  return api<BrandBookProfileView>(
    `/api/brand-books/${encodeURIComponent(brandBookId)}/profile`,
    signal ? { signal } : {},
  );
}

/** Replaces the whole profile; `{}` removes it. */
export async function saveBrandBookProfile(
  brandBookId: string,
  profile: ProductProfile,
): Promise<BrandBookProfileView> {
  return api<BrandBookProfileView>(`/api/brand-books/${encodeURIComponent(brandBookId)}/profile`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(profile),
  });
}

/* ------------------------------------------------- brand book context items */

/** What a context item is. Mirrors the server's `CONTEXT_ITEM_TYPES`. */
export type ContextItemType = 'url' | 'pdf' | 'docx' | 'image' | 'text';

/** One item of `GET /api/brand-books/:id/context-items`. Mirrors the server's `ContextItemView`. */
export interface ContextItem {
  id: string;
  brandBookId: string;
  type: ContextItemType;
  title: string;
  note: string | null;
  originalName: string | null;
  mime: string | null;
  bytes: number;
  url: string | null;
  /** `failed` is a web page that could not be read; `error` says why. */
  status: 'ready' | 'failed';
  error: string | null;
  createdAt: string;
  snapshotTakenAt: string | null;
}

/** Mirrors the server's limits in `brand/context-items.ts` and `db/brand-book-context-items.ts`. */
export const MAX_CONTEXT_ITEM_BYTES = 20 * 1024 * 1024;
export const MAX_CONTEXT_TEXT_CHARS = 200_000;
export const MAX_CONTEXT_ITEMS_PER_BOOK = 25;
export const MAX_CONTEXT_BYTES_PER_BOOK = 100 * 1024 * 1024;
export const MAX_CONTEXT_ITEM_TITLE_LENGTH = 120;
export const MAX_CONTEXT_ITEM_NOTE_LENGTH = 1000;
/** The server's JSON body cap (`express.json({ limit: '1mb' })`), which pasted text travels under. */
export const MAX_JSON_BODY_BYTES = 1024 * 1024;

/** The file extensions an upload is taken by. Mirrors the server's `TYPE_BY_EXTENSION`. */
export const CONTEXT_FILE_EXTENSIONS = [
  '.pdf',
  '.docx',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.svg',
  '.txt',
  '.md',
] as const;

function contextItemsPath(brandBookId: string, itemId?: string): string {
  const base = `/api/brand-books/${encodeURIComponent(brandBookId)}/context-items`;
  return itemId === undefined ? base : `${base}/${encodeURIComponent(itemId)}`;
}

/** Where the original of a file or text item is downloaded from. */
export function contextItemFileUrl(item: Pick<ContextItem, 'brandBookId' | 'id'>): string {
  return `${contextItemsPath(item.brandBookId, item.id)}/file`;
}

/** A web page item's screenshot. */
export function contextItemScreenshotUrl(item: Pick<ContextItem, 'brandBookId' | 'id'>): string {
  return `${contextItemsPath(item.brandBookId, item.id)}/snapshot/screenshot.png`;
}

export async function fetchContextItems(brandBookId: string, signal?: AbortSignal): Promise<ContextItem[]> {
  const { contextItems } = await api<{ contextItems: ContextItem[] }>(
    contextItemsPath(brandBookId),
    signal ? { signal } : {},
  );
  return contextItems;
}

/** The title and note an item is added with; an empty title lets the server name it. */
export interface ContextItemLabels {
  title: string;
  note: string;
}

function labelFields(labels: ContextItemLabels): { title?: string; note?: string } {
  const title = labels.title.trim();
  const note = labels.note.trim();
  return { ...(title === '' ? {} : { title }), ...(note === '' ? {} : { note }) };
}

/** A web page; the answer comes once the page has been read (`ready`) or could not be (`failed`). */
export async function addContextUrl(
  brandBookId: string,
  url: string,
  labels: ContextItemLabels,
): Promise<ContextItem> {
  return api<ContextItem>(contextItemsPath(brandBookId), {
    method: 'POST',
    body: JSON.stringify({ type: 'url', url: url.trim(), ...labelFields(labels) }),
  });
}

export function contextTextBody(text: string, labels: ContextItemLabels): string {
  return JSON.stringify({ type: 'text', text, ...labelFields(labels) });
}

export async function addContextText(
  brandBookId: string,
  text: string,
  labels: ContextItemLabels,
): Promise<ContextItem> {
  return api<ContextItem>(contextItemsPath(brandBookId), {
    method: 'POST',
    body: contextTextBody(text, labels),
  });
}

/**
 * A file, as multipart. `fetch` cannot report upload progress, so this one
 * request goes through `XMLHttpRequest`; it fails the way {@link api} does.
 */
export function uploadContextFile(
  brandBookId: string,
  file: File,
  labels: ContextItemLabels,
  onProgress: (sent: number, total: number) => void,
): Promise<ContextItem> {
  const form = new FormData();
  form.append('file', file);
  const fields = labelFields(labels);
  if (fields.title !== undefined) form.append('title', fields.title);
  if (fields.note !== undefined) form.append('note', fields.note);

  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('POST', contextItemsPath(brandBookId));
    request.responseType = 'text';
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(event.loaded, event.total);
    };
    request.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(request.responseText) as unknown;
      } catch {
        // Non-JSON body; the status says enough.
      }
      if (request.status >= 200 && request.status < 300) {
        resolve(body as ContextItem);
        return;
      }
      const shape = (body ?? {}) as { error?: unknown; message?: unknown };
      reject(
        new ApiError(
          request.status,
          typeof shape.error === 'string' ? shape.error : `http_${String(request.status)}`,
          typeof shape.message === 'string' ? shape.message : null,
        ),
      );
    };
    request.onerror = () => {
      reject(new Error('The upload did not reach the server.'));
    };
    request.send(form);
  });
}

export async function retryContextItem(item: Pick<ContextItem, 'brandBookId' | 'id'>): Promise<ContextItem> {
  return api<ContextItem>(`${contextItemsPath(item.brandBookId, item.id)}/retry`, { method: 'POST' });
}

/** A title cannot be removed; an empty note removes it. */
export async function updateContextItem(
  item: Pick<ContextItem, 'brandBookId' | 'id'>,
  patch: { title?: string; note?: string | null },
): Promise<ContextItem> {
  return api<ContextItem>(contextItemsPath(item.brandBookId, item.id), {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function deleteContextItem(item: Pick<ContextItem, 'brandBookId' | 'id'>): Promise<void> {
  await api(contextItemsPath(item.brandBookId, item.id), { method: 'DELETE' });
}


/** The profile's fields, in the order the server writes and shows them. */
export const PRODUCT_PROFILE_FIELDS = [
  'language',
  'address',
  'website',
  'descriptor',
  'cta',
  'buyer',
  'statusQuo',
  'buyerWords',
  'competitors',
  'objections',
  'proof',
  'noGos',
  'raiseToFail',
  'allowLines',
  'pronunciations',
  'voice',
  'cast',
  'rejectedVoices',
  'approvedLines',
  'removedApprovedLines',
  'rejectedLines',
  'notes',
] as const satisfies readonly (keyof ProductProfile)[];

export type ProductProfileField = (typeof PRODUCT_PROFILE_FIELDS)[number];

/** What saving one proposed field would do to what the content reads now. */
export type ProposalChange = 'new' | 'changed' | 'same';

/** Mirrors the server's `ProposalField`. */
export interface ProposalField {
  field: ProductProfileField;
  change: ProposalChange;
  /** The content's brand book's value, or `null`. */
  current: unknown;
  proposed: unknown;
  /** For a list, only the entries a save appends; otherwise the proposed value. */
  additions: unknown;
}

/**
 * Mirrors the server's `ProfileProposalView`: the product facts a planning
 * conversation learned (synthesis C7), beside the profile they would change.
 */
export interface ProfileProposal {
  contentId: string;
  exists: boolean;
  proposedAt: string | null;
  /** The content's brand book, which a save writes to; `null` for none. */
  brandBookId: string | null;
  brandBookName: string | null;
  current: ProductProfile;
  fields: ProposalField[];
  problems: { field: string; message: string }[];
  /** The last time planning ended and saved the facts on its own (US-014). */
  autoSaved: ProfileAutoSave | null;
}

/** Mirrors the server's `ProfileAutoSaveView`. */
export interface ProfileAutoSave {
  savedAt: string;
  brandBookId: string;
  /** `null` once the book is deleted. */
  brandBookName: string | null;
  /** The fields merged, each with what the book said before as `current`. */
  fields: ProposalField[];
  /** What the profile refused, left out. */
  skipped: { field: string; message: string }[];
  unchanged: ProductProfileField[];
  undoneAt: string | null;
  /** Why **Undo** is not offered, or `null` when it is. */
  undoBlocked: string | null;
}

export async function fetchProfileProposal(
  contentId: string,
  signal?: AbortSignal,
): Promise<ProfileProposal> {
  return api<ProfileProposal>(
    `/api/contents/${encodeURIComponent(contentId)}/profile-proposal`,
    signal ? { signal } : {},
  );
}

/** **Undo**: the brand book's product profile as it was before planning saved to it. */
export async function undoProfileAutoSave(
  contentId: string,
): Promise<{ saved: BrandBookProfileView; proposal: ProfileProposal }> {
  return api(`/api/contents/${encodeURIComponent(contentId)}/profile-proposal/undo`, { method: 'POST' });
}

/** **Dismiss**: the proposal is set aside unsaved. */
export async function dismissProfileProposal(contentId: string): Promise<ProfileProposal> {
  return api<ProfileProposal>(`/api/contents/${encodeURIComponent(contentId)}/profile-proposal`, {
    method: 'DELETE',
  });
}

/* ----------------------------------------------------------------- contents */

/** How a Dutch content addresses the viewer: informal *je* or formal *u*. */
export type ContentAddress = 'je' | 'u';

/** Whether a language tag is Dutch, which is the one language with an address. */
export function isDutchLanguage(language: string): boolean {
  return language.toLowerCase().split(/[-_]/)[0] === 'nl';
}

/** The name a language tag is shown by in a picker. */
export function languageLabel(language: string): string {
  const names: Record<string, string> = {
    'nl-NL': 'Dutch (Netherlands)',
    'nl-BE': 'Dutch (Belgium)',
    'en-US': 'English (US)',
    'en-GB': 'English (UK)',
    'de-DE': 'German',
    'fr-FR': 'French',
    'es-ES': 'Spanish',
    'it-IT': 'Italian',
  };
  return names[language] ?? language;
}

/**
 * Mirrors `CONTENT_STATUSES` on the server, which this tuple has to be kept in
 * step with by hand. The order is the lifecycle: `pending` until the plan is
 * written and marked ready, `producing` while the loop works through the
 * steps, `waiting` while it is held, `review` once a version exists and
 * `delivered` once the operator approved one; `rejected` when the operator
 * decided after delivery that it must not be used.
 *
 * A tuple rather than a bare union because the content list enumerates it to
 * offer one filter option per status.
 */
export const CONTENT_STATUSES = [
  'pending',
  'ready',
  'producing',
  'waiting',
  'approval',
  'review',
  'delivered',
  'rejected',
  'failed',
] as const;

export type ContentStatus = (typeof CONTENT_STATUSES)[number];

/**
 * Which step of the loop a `failed` content failed at, mirroring the server’s
 * `CONTENT_FAILURE_STAGES`. Null for every content that has not failed.
 */
export const CONTENT_FAILURE_STAGES = [
  'setup',
  'agent',
  'plan',
  'probe',
  'container_lost',
  'upload',
  'budget',
  'review',
] as const;

export type ContentFailureStage = (typeof CONTENT_FAILURE_STAGES)[number];

/**
 * What a failure stage is called in a row: the operator’s words, not the
 * enum’s. The same words as the server’s `stageLabel`, because a list that
 * calls it one thing and the retry plan beside it another is two answers; the
 * plan’s own `stageLabel` wins wherever one has been fetched.
 */
export function failureStageLabel(stage: ContentFailureStage | string): string {
  const labels: Record<ContentFailureStage, string> = {
    setup: 'setup',
    agent: 'the agent',
    plan: 'the plan file',
    probe: 'the output check',
    container_lost: 'a lost container',
    upload: 'the upload',
    budget: 'its spend cap',
    review: 'review',
  };
  return stage in labels ? labels[stage as ContentFailureStage] : stage;
}

/** Image or video: which production loop a content runs through. */
export const CONTENT_KINDS = ['image', 'video'] as const;

export type ContentKind = (typeof CONTENT_KINDS)[number];

/** What a repository is to a content: the one it is about, or background. */
export const CONTENT_REPOSITORY_ROLES = ['primary', 'context'] as const;

export type ContentRepositoryRole = (typeof CONTENT_REPOSITORY_ROLES)[number];

/** Mirrors the server's `ContentRepositoryView`. */
export interface ContentRepository {
  repositoryId: string;
  repositoryName: string;
  role: ContentRepositoryRole;
}

/** Which approval an `approval` content waits on: its narration, or its scenes. */
export type ContentGate = 'script' | 'sketch';

/** Mirrors the server's `ContentView`: a content as the API returns it. */
/** The brand book a content is made in, as the content answers it. */
/** A version a content is made with, and whether Mark ready pinned it. */
export interface ContentVersionPin {
  version: number;
  pinned: boolean;
}

export interface ContentBrandBook {
  id: string;
  name: string;
  sourceType: 'repository' | 'website' | 'manual';
  brandStatus: BrandStatus;
}

/** The funnel stages a content can serve, in the order the server returns them. */
export const FUNNEL_STAGES = ['cold', 'warm', 'hot', 'retention'] as const;
export type FunnelStage = (typeof FUNNEL_STAGES)[number];

/** One free key/value pair of brief details, such as `Vorm: sketch`. */
export interface ExtraDetail {
  key: string;
  value: string;
}

/**
 * The marketing side of a content: what it is for, who it is aimed at, the
 * funnel stages it serves, the angle, where it is published and whether it
 * has been. Texts are trimmed, and an empty one is `null`.
 */
export interface BriefDetails {
  goal: string | null;
  audience: string | null;
  angle: string | null;
  placement: string | null;
  funnelStages: FunnelStage[];
  placed: boolean;
  extraDetails: ExtraDetail[];
}

export interface Content {
  id: string;
  name: string;
  kind: ContentKind;
  status: ContentStatus;
  brief: string | null;
  /** One of the tones `GET /api/contents` offers. */
  tone: string | null;
  /** Free text refining the tone; never a substitute for it. */
  creativeDirection: string | null;
  formats: string[];
  /**
   * What the form was pre-filled with from Settings → Defaults and the
   * operator then left or changed (US-089). `null` is "nobody chose": a
   * content made before the panel existed, or a length on an image.
   */
  targetSeconds: number | null;
  captions: boolean | null;
  music: boolean | null;
  voiceover: boolean | null;
  watermark: boolean | null;
  /**
   * The video model this content's clips are generated on (US-085). `null` —
   * the ordinary state — follows the plan's own `**Model:**` line, and this
   * install's setting when the plan names none.
   */
  videoModel: string | null;
  /**
   * The language the content is written and spoken in, BCP-47, and for Dutch
   * whether it says je or u (synthesis C2). `null` on a content made before.
   */
  language: string | null;
  address: ContentAddress | null;
  /** `null` only for a content made before brand books that had nothing to point at. */
  brandBook: ContentBrandBook | null;
  /**
   * The ad kit and guidelines versions the content is made with: pinned at
   * Mark ready, else the current kit and the active guidelines it follows.
   */
  kitVersion: ContentVersionPin | null;
  guidelinesVersion: ContentVersionPin | null;
  repositories: ContentRepository[];
  /** Always present; a content nobody briefed has the empty set. */
  briefDetails: BriefDetails;
  /**
   * The content's own container while one is running, and the target a browser
   * terminal on the content page is opened into. Null before setup has run and
   * again once the container is removed; the workspace outlives it either way.
   */
  containerId: string | null;
  spendCapUsd: number | null;
  spentUsd: number;
  scheduledStartAt: string | null;
  /** The scheduled moment passed while the content was still pending. */
  scheduleMissed: boolean;
  queuedAt: string | null;
  queuePosition: number | null;
  waitingUntil: string | null;
  currentVersion: number;
  versionCount: number;
  /** The plan’s steps as `done` of `total`; `total` is 0 until a plan exists. */
  steps: { total: number; done: number };
  /** Pending, and `content/plan.md` parses: it waits on **Mark ready**. */
  planParses: boolean;
  /** Where the planning of a pending content stands; `null` outside `pending`. */
  planningState: PlanningState | null;
  /** The automatic review round in flight; `null` when none is. */
  autoReview: ContentAutoReview | null;
  /**
   * A **Request changes** pass is rewriting the plan from the operator's
   * feedback; the status reads `review` (or `delivered`) until it is done.
   */
  revising: boolean;
  /** Where the sketch gate stands; `preparing` and `adjusting` are Claude at work. */
  sketchState: SketchState | null;
  failureStage: ContentFailureStage | null;
  /** The gate an `approval` content is stopped at (US-093); `null` at none. */
  gate: ContentGate | null;
  /**
   * Plan and produce without waiting for the operator (one-shot ads US-020):
   * on, the narration gate is off. **Take over** switches it off.
   */
  autopilot: boolean;
  lastError: string | null;
  approvedAt: string | null;
  shareCopy: string | null;
  /** The campaign the content belongs to on the board; `null` for none. */
  campaignId: string | null;
  /** Publish-by date, `YYYY-MM-DD`. */
  dueOn: string | null;
  /** Why the content cannot move on; `null` when it is not blocked. */
  blockedReason: string | null;
  /** When the content was taken off the board. */
  archivedAt: string | null;
  /** When the operator rejected the content; `null` unless it is `rejected`. */
  rejectedAt: string | null;
  /** Why it was rejected, in the operator's words; `null` when no reason was given. */
  rejectionReason: string | null;
  /** How pausing the agent's Meta ads of a rejected content went; `null` unless it is `rejected`. */
  adPause: AdPauseView | null;
  /** Manual order inside a board column: lower is higher up. */
  boardRank: number;
  /** When the placed flag was switched on; `null` while it is off. */
  placedAt: string | null;
  checklist: ChecklistItem[];
  /** What its posts add up to: `due` counts planned posts whose time has passed. */
  posts: PostsSummary;
  createdAt: string;
  updatedAt: string;
}

/**
 * Where a pending content's planning stands: the terminal is open, Claude is
 * answering in the headless conversation, it asked the operator something, or
 * the conversation stopped. `none` is neither.
 */
export type PlanningState = 'none' | 'terminal' | 'thinking' | 'your-turn' | 'decided' | 'failed';

/** An agent ad of a rejected content that is still running, with Meta's answer to its last pause. */
export interface AdPauseFailure {
  adMetaId: string;
  adName: string;
  message: string;
}

/**
 * `Content.adPause`, mirroring the server's `AdPauseView`: `pausing` while
 * the background task runs, `failed` when it finished with ads still active,
 * `done` when none are, `none` when there was nothing to pause.
 */
export interface AdPauseView {
  state: 'none' | 'pausing' | 'done' | 'failed';
  failures: AdPauseFailure[];
}

/** `Content.autoReview`: the phase and number of the automatic round in flight. */
export interface ContentAutoReview {
  phase: AutoReviewPhase;
  round: number;
}

/** `Content.posts`: counts of a content's posts and the next scheduled one. */
export interface PostsSummary {
  planned: number;
  scheduled: number;
  posted: number;
  due: number;
  next: { platform: PostPlatform; scheduledAt: string } | null;
}

/** One line of a content's checklist, as `ContentView` carries it. */
export interface ChecklistItem {
  id: string;
  text: string;
  done: boolean;
}

/**
 * One tone as the picker shows it: the name, and the line it is chosen by.
 *
 * The summary is read out of the tone's own `rules/tones/<tone>.json` on the
 * server, so the wording here is never a second copy of it.
 */
export interface ToneOption {
  tone: string;
  summary: string;
  /** The display name, `Professional`; absent on an operator's own tone file. */
  label?: string;
  /** One line on where the tone fits: `Launch posts, investor updates, …`. */
  bestFor?: string;
}

/**
 * The name a tone is shown by: its `label`, or the file name for a tone that
 * has none (an operator's own file, or one deleted since the content chose it).
 */
export function toneLabel(tones: readonly ToneOption[] | null, tone: string): string {
  return tones?.find((option) => option.tone === tone)?.label ?? tone;
}

/**
 * The list, and the vocabulary the new-content form has to agree with: the
 * tones and the format presets this server will accept, in its own order.
 */
export interface ContentList {
  contents: Content[];
  tones: ToneOption[];
  formats: string[];
  /** The languages the form offers; any other tag is still accepted. */
  languages?: string[];
}

/** What creating a content sends. Anything omitted is the server's default. */
export interface ContentInput {
  name: string;
  kind: ContentKind;
  formats: string[];
  tone: string;
  /** The one brand book the content is made in; required. */
  brandBookId: string;
  brief?: string | null;
  creativeDirection?: string | null;
  /** The target length of a video in seconds; an image never carries one. */
  targetSeconds?: number;
  /** The Defaults toggles, as the form left them (US-089, US-090). */
  captions?: boolean;
  music?: boolean;
  voiceover?: boolean;
  approveScript?: boolean;
  watermark?: boolean;
  /** Plan and produce without waiting; omitted is the Settings default (one-shot ads US-020). */
  autopilot?: boolean;
  /** A model for this content alone; omitted follows the plan and Settings. */
  videoModel?: string | null;
  /** BCP-47; omitted takes the product profile's, then Settings'. */
  language?: string;
  /** Dutch only; `null` leaves it to the product profile. */
  address?: ContentAddress | null;
  /** `null` creates a content with no cap of its own. */
  spendCapUsd?: number | null;
  /** An instant, not a wall-clock time: the picker's value, converted. */
  scheduledStartAt?: string | null;
  /** Code sources only: product moments and screens, never the brand. */
  repositories?: { repositoryId: string; role: ContentRepositoryRole }[];
  /** Omitted is the empty set. */
  briefDetails?: BriefDetails;
  /** The campaign the content starts in; omitted or `null` for none. */
  campaignId?: string | null;
  /** The publish-by date, `YYYY-MM-DD`; omitted or `null` for none. */
  dueOn?: string | null;
}

/** Mirrors the server's `SetupResult`: what the clones did. */
export interface ContentSetup {
  ok: boolean;
  code: string;
  message: string;
  /** git's own output, empty when there was nothing to report. */
  stderr: string;
  repositoryId: string | null;
  cloned: string[];
}

/**
 * A created content and the setup that ran with it.
 *
 * A setup that failed is not a failed create: the content exists, `pending`,
 * with git's words in `setup` — which is why this answers 201 either way and
 * the form has to read `setup.ok` rather than the status code.
 */
export interface ContentWithSetup {
  content: Content;
  setup: ContentSetup | null;
}

export async function fetchContentList(signal?: AbortSignal): Promise<ContentList> {
  return api<ContentList>('/api/contents', signal ? { signal } : {});
}

/** One content, for the page that is only ever about that one. */
export async function fetchContent(id: string, signal?: AbortSignal): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

/** Creates the content and runs its setup: the skeleton, container and clones. */
export async function createContent(input: ContentInput): Promise<ContentWithSetup> {
  return api<ContentWithSetup>('/api/contents', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Runs the setup again: the skeleton, the container and one clone per
 * repository. Clones that already succeeded are reused, so this is the action
 * for a key that was fixed after the fact rather than a fresh start.
 */
export async function retryContentSetup(id: string): Promise<ContentWithSetup> {
  return api<ContentWithSetup>(`/api/contents/${encodeURIComponent(id)}/setup/retry`, {
    method: 'POST',
  });
}

/**
 * Sets — or clears (`null`) — this content's video model override (US-085).
 *
 * Clearing is not the same as choosing the install's model: a content with no
 * override follows the plan and then Settings, wherever they go next.
 */
export async function setVideoModel(id: string, videoModel: string | null): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/video-model`, {
    method: 'POST',
    body: JSON.stringify({ videoModel }),
  });
}

/** **Take over** (`false`) or **Hand back** (`true`) this one content (one-shot ads US-020). */
export async function setAutopilot(id: string, autopilot: boolean): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/autopilot`, {
    method: 'POST',
    body: JSON.stringify({ autopilot }),
  });
}

/**
 * Replaces all of a content's brief details at once, in any status. A field
 * the server refuses answers 400 `invalid_brief_details`, naming the field.
 */
export async function updateBriefDetails(id: string, details: BriefDetails): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/brief-details`, {
    method: 'PUT',
    body: JSON.stringify(details),
  });
}

/** Changes only the placed flag, for the list's quick toggle. */
export async function setPlaced(id: string, placed: boolean): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/placed`, {
    method: 'PUT',
    body: JSON.stringify({ placed }),
  });
}

/** Where a post can go; the server's `POST_PLATFORMS` in `server/src/db/posts.ts`. */
export type PostPlatform = SharePlatformId | 'other';

/** One place a content goes out, planned or marked posted. */
export interface Post {
  id: string;
  contentId: string;
  platform: PostPlatform;
  paid: boolean;
  label: string | null;
  /** A preset name from the content's formats. */
  format: string | null;
  status: 'planned' | 'posted';
  /** ISO-8601 UTC. */
  scheduledAt: string | null;
  postedAt: string | null;
  /** The content version that went out. */
  version: number | null;
  url: string | null;
  caption: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** What planning a post sends; every field but `platform` may be left out. */
export interface PostInput {
  platform: PostPlatform;
  paid?: boolean;
  label?: string | null;
  format?: string | null;
  scheduledAt?: string | null;
  caption?: string | null;
  notes?: string | null;
}

export async function fetchPosts(contentId: string, signal?: AbortSignal): Promise<Post[]> {
  const init: RequestInit = signal === undefined ? {} : { signal };
  const answer = await api<{ posts: Post[] }>(
    `/api/contents/${encodeURIComponent(contentId)}/posts`,
    init,
  );
  return answer.posts;
}

/** Plans a post. `other` needs a `label`; a `format` must be one of the content's. */
export async function createPost(contentId: string, input: PostInput): Promise<Post> {
  const answer = await api<{ post: Post }>(`/api/contents/${encodeURIComponent(contentId)}/posts`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return answer.post;
}

export async function updatePost(id: string, patch: Partial<PostInput>): Promise<Post> {
  const answer = await api<{ post: Post }>(`/api/posts/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
  return answer.post;
}

/**
 * Records that the post went out (at `postedAt`, now when left out) and places
 * the content. Only a delivered content: else 409 `content_not_delivered`.
 */
export async function markPostPosted(
  id: string,
  input: { postedAt?: string; url?: string | null } = {},
): Promise<{ post: Post; content: Content }> {
  return api<{ post: Post; content: Content }>(`/api/posts/${encodeURIComponent(id)}/posted`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Sets a posted post back to planned; the content is unplaced when none is left posted. */
export async function undoPostPosted(id: string): Promise<{ post: Post; content: Content }> {
  return api<{ post: Post; content: Content }>(`/api/posts/${encodeURIComponent(id)}/posted`, {
    method: 'DELETE',
  });
}

export async function deletePost(id: string): Promise<void> {
  await api<void>(`/api/posts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Clears the time of every planned post of the content. */
export async function unschedulePosts(contentId: string): Promise<Post[]> {
  const answer = await api<{ posts: Post[] }>(
    `/api/contents/${encodeURIComponent(contentId)}/posts/unschedule`,
    { method: 'POST' },
  );
  return answer.posts;
}

/** Undoes every posted post of the content and unplaces it. */
export async function unpostContent(contentId: string): Promise<{ posts: Post[]; content: Content }> {
  return api<{ posts: Post[]; content: Content }>(
    `/api/contents/${encodeURIComponent(contentId)}/unpost`,
    { method: 'POST' },
  );
}

/* ------------------------------------------------------- the board (US-010) */

/** A column of the board; the server's `BOARD_COLUMNS` in `server/src/board/columns.ts`. */
export type BoardColumn = 'ideas' | 'planning' | 'production' | 'review' | 'to-post' | 'scheduled' | 'posted';

/** The colours a campaign can take; the server's `CAMPAIGN_COLORS`. */
export type CampaignColor = 'blue' | 'teal' | 'green' | 'amber' | 'red' | 'purple' | 'gray';

/** Mirrors the server's `CampaignView`: a campaign with what is in it. */
export interface Campaign {
  id: string;
  name: string;
  color: CampaignColor;
  brandBookId: string | null;
  /** `YYYY-MM-DD`. */
  startsOn: string | null;
  endsOn: string | null;
  goal: string | null;
  notes: string | null;
  /** The checklist a content gets when it joins the campaign. */
  checklist: string[];
  /** One approved still per character and per location (one-shot ads US-029). */
  plates: CampaignPlate[];
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
  counts: { ideas: number; contents: number; posted: number };
}

/** One approved still of a campaign: every picture of that person or place is made from it. */
export interface CampaignPlate {
  kind: 'location' | 'character';
  /** As the plan's `## Staging` names the person or the place. */
  name: string;
  libraryItemId: string;
}

/** What a campaign is made or changed with (`POST` / `PATCH /api/campaigns`). */
export interface CampaignInput {
  /** At most 60 characters, unique whatever the case. */
  name: string;
  color: CampaignColor;
  brandBookId: string | null;
  /** `YYYY-MM-DD`, or `null` for none. */
  startsOn: string | null;
  endsOn: string | null;
  /** At most 500 characters; blank is none. */
  goal: string | null;
  /** At most 2000 characters; blank is none. */
  notes: string | null;
  /** At most 20 items of 1 to 120 characters. */
  checklist: string[];
  /** At most 40, one per kind and name, each an image of the library. */
  plates: CampaignPlate[];
}

/** Every campaign, archived ones included. */
export async function fetchCampaigns(signal?: AbortSignal): Promise<Campaign[]> {
  const answer = await api<{ campaigns: Campaign[] }>('/api/campaigns', signal ? { signal } : {});
  return answer.campaigns;
}

/** Makes a campaign; a name already taken answers 409 `campaign_name_taken`. */
export async function createCampaign(input: CampaignInput): Promise<Campaign> {
  const answer = await api<{ campaign: Campaign }>('/api/campaigns', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return answer.campaign;
}

/** Changes a campaign's fields. */
export async function updateCampaign(id: string, input: Partial<CampaignInput>): Promise<Campaign> {
  const answer = await api<{ campaign: Campaign }>(`/api/campaigns/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return answer.campaign;
}

/** Archives a campaign, or brings it back. */
export async function setCampaignArchived(id: string, archived: boolean): Promise<Campaign> {
  const answer = await api<{ campaign: Campaign }>(`/api/campaigns/${encodeURIComponent(id)}/archive`, {
    method: archived ? 'POST' : 'DELETE',
  });
  return answer.campaign;
}

/** Deletes a campaign; its cards keep everything else and lose the campaign. */
export async function deleteCampaign(id: string): Promise<void> {
  await api(`/api/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** One card: an idea still to decide, or a content in the column it stands in. */
export type BoardCard =
  | {
      type: 'idea';
      cardId: string;
      column: 'ideas';
      idea: Idea;
      batch: { id: string; title: string; brandBookId: string | null };
    }
  | {
      type: 'content';
      cardId: string;
      column: BoardColumn;
      content: Content;
      posts: Post[];
      /** The picture the card shows; `null` for none. */
      posterAssetId: string | null;
    };

/** Mirrors the server's `BoardView`: everything the board draws, in one read. */
export interface BoardView {
  cards: BoardCard[];
  /** Every campaign, archived ones included. */
  campaigns: Campaign[];
  brandBooks: { id: string; name: string }[];
  generatedAt: string;
}

/**
 * The whole board. Archived contents and contents posted more than 30 days
 * ago are left out unless asked for.
 */
export async function fetchBoard(
  options: { archived?: boolean; postedAll?: boolean; signal?: AbortSignal } = {},
): Promise<BoardView> {
  const query = new URLSearchParams();
  if (options.archived === true) query.set('archived', '1');
  if (options.postedAll === true) query.set('posted', 'all');
  const search = query.toString();
  return api<BoardView>(
    `/api/board${search === '' ? '' : `?${search}`}`,
    options.signal ? { signal: options.signal } : {},
  );
}

/** One post on the calendar (US-011): the post, and enough of its content to name it. */
export interface CalendarPost extends Post {
  contentName: string;
  contentKind: ContentKind;
  contentStatus: ContentStatus;
  campaignId: string | null;
}

/** A publish-by date on the calendar: an undecided idea, or a content not placed yet. */
export interface CalendarDue {
  cardId: string;
  title: string;
  /** `YYYY-MM-DD`, a calendar day. */
  dueOn: string;
  column: BoardColumn;
  campaignId: string | null;
}

/** Mirrors the server's `CalendarView`: what the calendar draws for one range. */
export interface CalendarView {
  posts: CalendarPost[];
  /** Every publish-by date that still matters, whatever the range. */
  due: CalendarDue[];
}

/**
 * The posts in `[from, to)` and the publish-by dates (`GET /api/calendar`).
 * `from` and `to` are instants at most 62 days apart.
 */
export async function fetchCalendar(
  from: string,
  to: string,
  options: { signal?: AbortSignal } = {},
): Promise<CalendarView> {
  const query = new URLSearchParams({ from, to });
  return api<CalendarView>(`/api/calendar?${query.toString()}`, options.signal ? { signal: options.signal } : {});
}

/** The fields a card carries whatever it is (`PATCH /api/board/cards/:cardId`). */
export interface CardFieldsUpdate {
  campaignId?: string | null;
  /** `YYYY-MM-DD`, or `null` to clear it. */
  dueOn?: string | null;
  /** At most 200 characters; `null` or blank clears it. */
  blockedReason?: string | null;
}

/** Changes a card's campaign, publish-by date or blocked reason; answers the card as it now is. */
export async function updateCardFields(
  cardId: string,
  update: CardFieldsUpdate,
): Promise<{ content: Content } | { idea: Idea }> {
  return api<{ content: Content } | { idea: Idea }>(`/api/board/cards/${encodeURIComponent(cardId)}`, {
    method: 'PATCH',
    body: JSON.stringify(update),
  });
}

/**
 * Places a card between two others of its type (`PUT /api/board/cards/:cardId/rank`):
 * only `aboveCardId` puts it under that card, only `belowCardId` over it.
 */
export async function rankCard(
  cardId: string,
  neighbours: { aboveCardId: string | null; belowCardId: string | null },
): Promise<{ content: Content } | { idea: Idea }> {
  return api<{ content: Content } | { idea: Idea }>(`/api/board/cards/${encodeURIComponent(cardId)}/rank`, {
    method: 'PUT',
    body: JSON.stringify(neighbours),
  });
}

/** Replaces a content's whole checklist. An item without an id gets one. */
export async function replaceChecklist(
  id: string,
  items: readonly { id?: string; text: string; done: boolean }[],
): Promise<Content> {
  const answer = await api<{ content: Content }>(`/api/contents/${encodeURIComponent(id)}/checklist`, {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
  return answer.content;
}

/**
 * Archives a content, or brings it back. Archiving a content that is
 * producing or waiting answers 409 `content_running`.
 */
export async function setContentArchived(id: string, archived: boolean): Promise<Content> {
  const answer = await api<{ content: Content }>(`/api/contents/${encodeURIComponent(id)}/archive`, {
    method: archived ? 'POST' : 'DELETE',
  });
  return answer.content;
}

/**
 * Rejects a delivered content, with an optional reason. Its agent's live Meta
 * ads are paused in the background; `adPause` on the answer says how that goes.
 */
export async function rejectContent(id: string, reason: string): Promise<Content> {
  const trimmed = reason.trim();
  const answer = await api<{ content: Content }>(`/api/contents/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    body: JSON.stringify(trimmed === '' ? {} : { reason: trimmed }),
  });
  return answer.content;
}

/** Makes a rejected content delivered again. Paused ads stay paused. */
export async function unrejectContent(id: string): Promise<Content> {
  const answer = await api<{ content: Content }>(`/api/contents/${encodeURIComponent(id)}/unreject`, {
    method: 'POST',
  });
  return answer.content;
}

/** Pauses again the ads of a rejected content that are still active after a failed pause. */
export async function retryContentAdPauses(id: string): Promise<Content> {
  const answer = await api<{ content: Content }>(
    `/api/contents/${encodeURIComponent(id)}/reject/retry-pauses`,
    { method: 'POST' },
  );
  return answer.content;
}

/** Copies a content under a new name (default `<name>-copy`) and sets the copy up. */
export async function duplicateContent(id: string, name?: string): Promise<ContentWithSetup> {
  return api<ContentWithSetup>(`/api/contents/${encodeURIComponent(id)}/duplicate`, {
    method: 'POST',
    body: JSON.stringify(name === undefined ? {} : { name }),
  });
}

/** Sets the language, and for Dutch the address, of a content that exists. */
export async function setContentLanguage(
  id: string,
  language: string,
  address: ContentAddress | null,
): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/language`, {
    method: 'POST',
    body: JSON.stringify({ language, address }),
  });
}

/** One video call of a content, as the ledger recorded it (US-085). */
export interface ClipLedgerRow {
  id: string;
  operation: string;
  model: string | null;
  /** Seconds of video billed; `null` for a call metered some other way. */
  seconds: number | null;
  unit: string | null;
  costUsd: number;
  status: 'ok' | 'failed' | 'cached' | 'refused';
  stepId: number | null;
  summary: string | null;
  error: string | null;
  createdAt: string;
}

/** Every clip made on one model, and what they came to together. */
export interface ClipLedgerModel {
  model: string;
  clips: number;
  seconds: number;
  costUsd: number;
  /** `null` when no clip on this model was ever made — never 0. */
  usdPerClip: number | null;
  usdPerSecond: number | null;
}

export interface ClipLedger {
  contentId: string;
  rows: ClipLedgerRow[];
  models: ClipLedgerModel[];
  totalUsd: number;
  clips: number;
}

/** One entry of a content's history (one-shot ads US-022), from the server's `ContentEvent`. */
export interface ContentEvent {
  id: number;
  contentId: string;
  kind:
    | 'marked_ready'
    | 'queued'
    | 'not_queued'
    | 'plan_sent_back'
    | 'repair_undone'
    | 'scenes_approved'
    | 'scenes_sent_back'
    | 'scenes_not_approved'
    | 'waited_for_operator'
    | 'rejected'
    | 'unrejected';
  /** "Marked ready automatically", "Queued automatically", … */
  title: string;
  detail: string | null;
  createdAt: string;
}

/** What the server did on the operator's behalf, oldest first. */
export async function fetchContentHistory(id: string, signal?: AbortSignal): Promise<ContentEvent[]> {
  const body = await api<{ events: ContentEvent[] }>(
    `/api/contents/${encodeURIComponent(id)}/history`,
    signal ? { signal } : {},
  );
  return body.events;
}

/** What this content's clips cost, per clip and per model (US-085). */
export async function fetchClipLedger(id: string, signal?: AbortSignal): Promise<ClipLedger> {
  return api<ClipLedger>(
    `/api/contents/${encodeURIComponent(id)}/ledger`,
    signal ? { signal } : {},
  );
}

/**
 * Raises this content's spend cap, and nothing else (US-061).
 *
 * The **Raise cap and retry** button on a content that failed at stage
 * `budget` calls this and then the ordinary retry, so the two remain separate:
 * a cap can be raised without starting anything.
 */
export async function raiseSpendCap(id: string, spendCapUsd: number): Promise<Content> {
  return api<Content>(`/api/contents/${encodeURIComponent(id)}/spend-cap`, {
    method: "POST",
    body: JSON.stringify({ spendCapUsd }),
  });
}

/** Removes the content, its container and its workspace. Repositories are untouched. */
export async function deleteContent(id: string): Promise<void> {
  await api<void>(`/api/contents/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* --------------------------------------------------------------- planning */

/** One thing the parser could not accept, at the line of the plan it is on. */
export interface PlanProblem {
  /** 1-based; 0 only when there is nothing in the file to point at. */
  line: number;
  message: string;
}

/** Which layer of `rules/limits.json`'s precedence a value came from. */
export type RuleOrigin = 'built-in' | 'settings' | 'plan';

/**
 * Mirrors the server's `MergedRuleView`: one rule in force, already rendered.
 * The web holds no copy of a limit and no arithmetic — the value, the wording
 * of where it came from and the clamp are all the server's answers.
 */
export interface MergedRule {
  /** Dotted path, as `limits.json` and a plan's `**Rules:**` line spell it. */
  field: string;
  value: string;
  origin: RuleOrigin;
  /** In words: `the built-in professional tone`, `Settings → Tones → professional`. */
  source: string;
  /** What was asked for, when a hard floor or ceiling cut it back. */
  clampedFrom: string | null;
}

/** Mirrors the server's `PlanStatus`: what `content/plan.md` says right now. */
export interface PlanStatus {
  /** Relative to the workspace root, which is how the panel names it. */
  path: string;
  exists: boolean;
  /** True only when the file exists *and* has no errors. */
  parses: boolean;
  stepCount: number;
  /** `## Storyboard` scenes the arithmetic was run over. */
  sceneCount: number;
  /** The rules the plan was measured against, and where each one came from. */
  rules: MergedRule[];
  errors: PlanProblem[];
  /** Worth saying; they do not stop the content being marked ready. */
  warnings: PlanProblem[];
  /** The plan's `## Decisions made` (US-021); read even before the plan parses. */
  decisions: PlanDecision[];
  /** The plan's `## Claims` ledger (the ad bible US-014). */
  claims: PlanClaim[];
  /** What the ledger does not back: the planner's to repair, never a hold on Mark ready. */
  repairs: PlanRepair[];
  updatedAt: string | null;
  bytes: number;
}

/** One row of the claims ledger: `| Claim | Kind | Source | Scope | Permission | Status |`. */
export interface PlanClaim {
  readonly claim: string;
  readonly kind: string;
  readonly source: string;
  readonly scope: string;
  readonly permission: string;
  readonly status: string;
  readonly line: number;
}

/** A claim the ledger does not back, or a still of a cast character without their reference, for the planner to repair. */
export interface PlanRepair {
  readonly code: 'claim_unbacked' | 'cast_reference_missing' | 'hook_repeated' | 'gaze_missing' | 'gaze_to_camera_untagged';
  readonly line: number;
  readonly message: string;
}

/** One decision an autopilot planner took itself: "question — chosen — why — source". */
export interface PlanDecision {
  readonly question: string;
  readonly chosen: string;
  readonly why: string;
  readonly source: string;
  readonly line: number;
}

/** Mirrors the server's `STEP_TYPES`: what a step of the plan produces. */
export type StepType =
  | 'research'
  | 'brand'
  | 'capture'
  | 'image'
  | 'animate'
  | 'talk'
  | 'script'
  | 'voiceover'
  | 'music'
  | 'sfx'
  | 'clip'
  | 'edit'
  | 'captions'
  | 'compose'
  | 'render'
  | 'review'
  | 'copy'
  | 'variant';

/** Mirrors the server's `STEP_STATUSES`. */
export type StepStatus = 'todo' | 'in-progress' | 'done';

/** Mirrors the server's `PlanStepView`: one step as the page lists it. */
export interface PlanStep {
  /** `ST-001`, the plan's own identifier. Never renumbered. */
  stepId: string;
  title: string;
  type: StepType;
  /** Lower runs first. */
  priority: number;
  status: StepStatus;
  /** Declared output paths, relative to `content/`. */
  outputs: string[];
  /** A type that can only be produced through a paid provider (PLAN §12). */
  usesProvider: boolean;
  /** Production attempts so far; 0 for a step that is still only in the file. */
  attempts: number;
  /**
   * What the server measured about this step's outputs on the last attempt
   * (PLAN §8), or `null` when nothing has been probed yet.
   */
  probe: ProbeReport | null;
  /**
   * When production last started this step and when that attempt ended, with
   * the duration of the last finished one. All null for a step that is still
   * only in the plan file; a step running right now has `startedAt` and no
   * `durationMs`, which is what the step list times it from.
   */
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  /**
   * What the step's last agent run cost Claude, or `null` when it reported
   * nothing — a step that has not run, or one whose agent was reaped before it
   * could say. It is not part of `estimate`: that is the provider ledger,
   * measured against the spend cap, and this is subscription usage.
   */
  claude: StepClaudeUsage | null;
}

/** Mirrors the server's `StepClaudeUsage`: one step's share of the Claude bill. */
export interface StepClaudeUsage {
  /** What the run would have cost at API prices; the CLI's own figure. */
  costUsd: number | null;
  numTurns: number | null;
  tokens: ClaudeTokens | null;
}

/** Mirrors the server's `AgentTokens`: the tokens behind one step's cost. */
export interface ClaudeTokens {
  input: number | null;
  output: number | null;
  cacheRead: number | null;
  cacheCreation: number | null;
  /** Every count above that was reported, added up. */
  total: number | null;
}

/** Mirrors the server's `ProbeKind`: which probe a declared output got. */
export type ProbeKind = 'audio' | 'video' | 'image' | 'existence';

/** Mirrors the server's `OutputProbe`: what one declared output measured. */
export interface OutputProbe {
  /** The declared path, relative to `content/`. */
  path: string;
  kind: ProbeKind;
  exists: boolean;
  bytes: number | null;
  durationMs: number | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  sampleRateHz: number | null;
  /** Integrated loudness, as `loudnorm` measured it. */
  loudnessLufs: number | null;
  truePeakDbtp: number | null;
  videoCodec: string | null;
  audioCodec: string | null;
  errors: string[];
  ok: boolean;
}

/** Mirrors the server's `ProbeCheck`: a check about the step as a whole. */
export interface ProbeCheck {
  /** What was run, as the operator would recognise it: `hyperframes check`. */
  name: string;
  ok: boolean;
  /** `null` when the check was cut short by its timeout. */
  exitCode: number | null;
  output: string;
  /** What the check noticed that does not fail the step. */
  warnings?: string[];
}

/**
 * Mirrors the server's `ProbeReport`: everything it measured about one
 * attempt at one step. A step is only ever `done` with a clean report behind
 * it, so a report with errors in it is why a step went back to `in-progress`.
 */
export interface ProbeReport {
  stepId: string;
  type: StepType;
  checkedAt: string;
  ok: boolean;
  outputs: OutputProbe[];
  checks: ProbeCheck[];
  errors: string[];
  /** Every check's warnings; absent when there are none. */
  warnings?: string[];
}

/** Mirrors the server's `PROVIDER_STEP_TYPES`: the types that can cost money. */
export type ProviderStepType = 'image' | 'clip' | 'voiceover' | 'animate' | 'talk';

/** Which cap an estimate was measured against. */
export type SpendCapSource = 'content' | 'settings';

/** Mirrors the server's `EstimateLine`: one priced type of step. */
export interface EstimateLine {
  type: ProviderStepType;
  steps: number;
  /** Rounded to cents. */
  usd: number;
  /** The model and route the price is quoted for. */
  provider: string;
  /** The arithmetic in words, e.g. `2 clips × 8 seconds × $0.05`. */
  basis: string;
  /** Shown instead of the type's label, e.g. `Re-roll allowance` after the animate line. */
  label?: string;
}

/**
 * Mirrors the server's `CostEstimate`: roughly what producing a plan will
 * cost, and every assumption behind the figure. The prices are a static sheet
 * in `server/src/gateway/prices.ts`, so this is an order of magnitude rather
 * than a quote — which is why an estimate over the cap is a warning and never
 * a refusal.
 */
export interface CostEstimate {
  usd: number;
  lines: EstimateLine[];
  providerSteps: number;
  freeSteps: number;
  totalSteps: number;
  /** The cap it was compared against; null when none is in force. */
  capUsd: number | null;
  capSource: SpendCapSource | null;
  overCap: boolean;
  /** Operator-facing sentences behind the number, in reading order. */
  assumptions: string[];
  /** The date the price sheet was read. */
  pricedOn: string;
}

/**
 * Where the listed steps came from: `plan` is the file a pending content is
 * still being planned into — a preview of what **Mark ready** would promote —
 * and `steps` is the content's own rows, which is what production runs.
 */
export type PlanStepSource = 'plan' | 'steps';

/** Mirrors the server's `PlanView`: the steps, and the file behind them. */
export interface ContentPlan {
  contentId: string;
  plan: PlanStatus;
  /** The file exactly as written, for the raw view; null when there is none. */
  markdown: string | null;
  steps: PlanStep[];
  source: PlanStepSource;
  /** What producing these steps is expected to cost, before anything is spent. */
  estimate: CostEstimate;
}

/** Which of the two prompts a planning terminal was (or would be) started with. */
export type PlanningMode = 'create' | 'edit';

/** Mirrors the server's `PlanningView`: the terminal, and the plan beside it. */
export interface Planning {
  contentId: string;
  contentName: string;
  status: ContentStatus;
  /** The terminal to attach to, or null when planning was never started. */
  terminalId: string | null;
  running: boolean;
  exitCode: number | null;
  mode: PlanningMode | null;
  /** What starting one *now* would use: `edit` once a plan exists. */
  nextMode: PlanningMode;
  cwd: string;
  plan: PlanStatus;
  /** What the brand profiles behind this content are doing (US-071). */
  brand: PlanningBrand;
}

/**
 * Mirrors the server's `PlanningBrandStatus`: whether starting the
 * conversation would wait for an extraction first, and what it would go
 * without if one has failed.
 */
export interface PlanningBrand {
  /** Repositories whose profile is being extracted right now, by name. */
  extracting: string[];
  /** True when starting planning waits for one of them. */
  waiting: boolean;
  /** One line about the wait, or null when there is nothing to wait for. */
  message: string | null;
  /** What planning proceeds without after a failed extraction; null if none. */
  warning: string | null;
}

/**
 * What **Mark ready** answers with.
 *
 * A plan that does not parse is the ordinary state of an unfinished
 * conversation, not a failed request, so the server answers 200 either way and
 * the page reads `ok` rather than the status code — the same shape as
 * {@link ContentWithSetup}.
 */
export type MarkReadyResult =
  | { ok: true; content: Content; plan: PlanStatus; estimate: CostEstimate }
  | { ok: false; plan: PlanStatus; errors: PlanProblem[] };

/** Polled by the content page: whether `claude` is alive, and what it wrote. */
export async function fetchPlanning(id: string, signal?: AbortSignal): Promise<Planning> {
  return api<Planning>(
    `/api/contents/${encodeURIComponent(id)}/planning`,
    signal ? { signal } : {},
  );
}

/** The steps list and the raw plan, polled beside the planning status. */
export async function fetchContentPlan(id: string, signal?: AbortSignal): Promise<ContentPlan> {
  return api<ContentPlan>(`/api/contents/${encodeURIComponent(id)}/plan`, signal ? { signal } : {});
}

/**
 * Opens the planning conversation in the content's own container, in
 * `/workspace`. Safe to call twice: a running terminal is rejoined rather than
 * replaced, which is also what a second browser tab gets.
 */
export async function startPlanning(id: string, context?: string): Promise<Planning> {
  return api<Planning>(`/api/contents/${encodeURIComponent(id)}/planning/start`, {
    method: 'POST',
    body: JSON.stringify(context === undefined || context === '' ? {} : { context }),
  });
}

/** Where the headless planner is; `idle` is a content no exchange ever ran for. */
export type HeadlessStatus = 'idle' | 'running' | 'done' | 'failed';

/** The headless planning conversation (synthesis C0): its latest exchange, as `…/planning/headless` answers it. */
export interface HeadlessPlanning {
  contentId: string;
  status: HeadlessStatus;
  sessionId: string | null;
  mode: 'create' | 'edit' | null;
  /** How many exchanges the session has had, the running one included. */
  exchange: number;
  /** What the latest exchange sent. */
  message: string | null;
  /** The planner's final message of the latest finished exchange. */
  reply: string | null;
  planWritten: boolean;
  planParses: boolean;
  planErrors: number;
  planChanged: boolean;
  /** On autopilot the planner decides instead of asking (US-021). */
  autopilot: boolean;
  decisions: PlanDecision[];
  /** The latest exchange's cost; `null` when the CLI printed none. */
  costUsd: number | null;
  turns: number | null;
  totalCostUsd: number;
  startedAt: string | null;
  finishedAt: string | null;
  /** Why the latest exchange failed, in one sentence. */
  error: string | null;
  log: string;
}

/** Polled by the planning conversation panel (US-023) for the planner's reply. */
export async function fetchHeadlessPlanning(id: string, signal?: AbortSignal): Promise<HeadlessPlanning> {
  return api<HeadlessPlanning>(
    `/api/contents/${encodeURIComponent(id)}/planning/headless`,
    signal ? { signal } : {},
  );
}

/**
 * Sends one message to the headless planner (synthesis C0); `{}` opens the
 * conversation with the content's own brief. Answers 202 with the exchange
 * running; the panel that reads the reply polls `GET …/planning/headless`.
 */
export async function startHeadlessPlanning(id: string, message?: string): Promise<HeadlessPlanning> {
  return api<HeadlessPlanning>(`/api/contents/${encodeURIComponent(id)}/planning/headless`, {
    method: 'POST',
    body: JSON.stringify(message === undefined ? {} : { message }),
  });
}

/** Stops the exchange in flight; the session is kept for the next message. */
export async function stopHeadlessPlanning(id: string): Promise<HeadlessPlanning> {
  return api<HeadlessPlanning>(`/api/contents/${encodeURIComponent(id)}/planning/headless`, {
    method: 'DELETE',
  });
}

/** Ends the conversation: kills `claude` and forgets the terminal. */
export async function stopPlanning(id: string): Promise<Planning> {
  return api<Planning>(`/api/contents/${encodeURIComponent(id)}/planning/stop`, {
    method: 'POST',
  });
}

/** Parses the plan, syncs the steps and promotes the content to `ready`. */
export async function markContentReady(id: string): Promise<MarkReadyResult> {
  return api<MarkReadyResult>(`/api/contents/${encodeURIComponent(id)}/ready`, { method: 'POST' });
}

/** Sends a `ready` content back to `pending` so its plan can be changed. */
export async function backToPlanning(id: string): Promise<Content> {
  const { content } = await api<{ ok: boolean; content: Content }>(
    `/api/contents/${encodeURIComponent(id)}/ready`,
    { method: 'DELETE' },
  );
  return content;
}

/** `/content/<id>`, the one place a content is looked at. */
export function contentPath(id: string): string {
  return `/content/${encodeURIComponent(id)}`;
}

/* ------------------------------------------------------------ format presets */

/**
 * Mirrors the server's `FormatPreset`, which is one
 * `rules/formats/<preset>.json` as loaded and schema-validated at boot.
 *
 * A format says how big the output is and where it is safe to put things. The
 * same numbers drive the plan, the composition, the lint pass and the render
 * probes, so nothing in the UI recomputes one of them — the preview frames, the
 * safe-area overlays and the "this is too long for TikTok" warnings all read
 * this answer.
 */
export interface FormatPreset {
  /** The preset name, which is also its file name — `reel`, `og`, … */
  preset: string;
  /** Whether the preset is a render target for video, for stills, or both. */
  kinds: FormatPresetKind[];
  width: number;
  height: number;
  /** `W:H`, e.g. `9:16`. */
  aspect: string;
  /** Absent on image-only presets. */
  fps?: number;
  /** Where the output is published, e.g. `tiktok`, `open-graph`. */
  platforms: string[];
  limits: FormatLimits;
  /** Fractions of each side that platform UI may cover. */
  safeArea: FormatSafeArea;
  /** Smallest legible size per text role, in pixels at the preset's size. */
  minFontPx: FormatMinFontPx;
  /** Absent on presets that carry no captions. */
  captionZone?: FormatCaptionZone;
  /** Why the numbers are what they are; shown, never enforced. */
  notes?: string;
}

export type FormatPresetKind = 'video' | 'image';

/** The platform's hard limits. Only `maxMB` is on every preset. */
export interface FormatLimits {
  minSeconds?: number;
  maxSeconds?: number;
  maxMB: number;
  imageFormats?: string[];
}

export interface FormatSafeArea {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface FormatMinFontPx {
  body: number;
  title: number;
  caption?: number;
}

/** Vertical band, in fractions of the height from the top, for captions. */
export interface FormatCaptionZone {
  top: number;
  bottom: number;
}

/** `1080 × 1920 · 9:16 · 30 fps`: one preset as a single line of chrome. */
export function describeFormatPreset(preset: FormatPreset): string {
  const parts = [`${String(preset.width)} × ${String(preset.height)}`, preset.aspect];
  if (preset.fps !== undefined) parts.push(`${String(preset.fps)} fps`);
  if (preset.limits.maxSeconds !== undefined) parts.push(`≤ ${String(preset.limits.maxSeconds)}s`);
  parts.push(`≤ ${String(preset.limits.maxMB)} MB`);
  return parts.join(' · ');
}

/**
 * The built-in presets, in the server's own order. Loaded once from the rules
 * files at boot, so this never changes while the app is open.
 */
export async function fetchPresets(signal?: AbortSignal): Promise<FormatPreset[]> {
  const body = await api<{ presets: FormatPreset[] }>(
    '/api/presets',
    signal ? { signal } : {},
  );
  return body.presets;
}

/* ---------------------------------------------------------------- terminals */

export interface Container {
  id: string;
  name: string;
  image: string;
  state: string;
  status: string;
}

/** Mirrors the server's `TerminalView`. */
export interface Terminal {
  id: string;
  container: string;
  containerName: string;
  command: string[];
  status: 'running' | 'exited';
  exitCode: number | null;
  cols: number;
  rows: number;
  clients: number;
  scrollbackBytes: number;
  createdAt: string;
  lastActivityAt: string;
}

export interface TerminalInput {
  container: string;
  /** Omit to let the server start a login shell (bash, falling back to sh). */
  command?: string[];
  cwd?: string;
  cols?: number;
  rows?: number;
}

export async function fetchContainers(signal?: AbortSignal): Promise<Container[]> {
  const body = await api<{ containers: Container[] }>('/api/containers', signal ? { signal } : {});
  return body.containers;
}

export async function fetchTerminals(signal?: AbortSignal): Promise<Terminal[]> {
  const body = await api<{ terminals: Terminal[] }>('/api/terminals', signal ? { signal } : {});
  return body.terminals;
}

export async function createTerminal(input: TerminalInput): Promise<Terminal> {
  return api<Terminal>('/api/terminals', { method: 'POST', body: JSON.stringify(input) });
}

/** Kills the process inside the container and forgets the terminal. */
export async function closeTerminal(id: string): Promise<void> {
  await api<void>(`/api/terminals/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * WebSocket URL for a terminal's PTY. Same origin as the page, so the session
 * cookie is sent with the handshake and the gateway can authenticate it.
 */
export function terminalSocketUrl(id: string): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/terminals/${encodeURIComponent(id)}/stream`;
}

/* ----------------------------------------------------------- production log */

/** One step attempt's section of `content/agent.log`. */
export interface ProductionLogStep {
  /** The run's iteration counter; a retried step gets a second section. */
  iteration: number;
  stepId: string | null;
  startedAt: string;
  /** `null` while the step is still running. */
  endedAt: string | null;
  exitCode: number | null;
  text: string;
}

/** Everything the log holds, as the server read it back off the file. */
export interface ProductionLogHistory {
  /** The file in the workspace, so the panel can name what it is showing. */
  path: string;
  steps: ProductionLogStep[];
  /** True when older output was dropped because the file is long. */
  truncated: boolean;
}

/** What the server reports as the log grows. */
export type ProductionLogEvent =
  | { type: 'begin'; iteration: number; stepId: string | null; startedAt: string }
  | { type: 'append'; text: string }
  | { type: 'end'; exitCode: number | null; endedAt: string };

/** Mirrors the server's `ProductionLogMessage`: replay first, then live events. */
export type ProductionLogMessage =
  | { type: 'attached'; history: ProductionLogHistory }
  | ProductionLogEvent;

/**
 * WebSocket URL for a content's production log. Same origin as the page, so
 * the session cookie rides along with the handshake (see
 * {@link terminalSocketUrl}).
 */
export function productionLogSocketUrl(id: string): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/contents/${encodeURIComponent(id)}/log`;
}

/* ------------------------------------------------------------------- limits */

/**
 * "Resume now": lifts Claude's usage-limit hold before its hour is up and puts
 * every held content back to work, not only the one being looked at — the hold
 * is on the account, so there is nothing narrower to lift. Answers with how
 * many productions were actually started; the rest are on the queue.
 */
export async function clearUsageLimitHold(): Promise<{ resumed: number }> {
  return api<{ ok: true; resumed: number }>('/api/limits/hold/clear', { method: 'POST' });
}

/** The global usage-limit hold, if one is in force. */
export async function fetchHold(signal?: AbortSignal): Promise<{ until: string | null }> {
  return api<{ until: string | null }>('/api/limits/hold', signal ? { signal } : {});
}

/* -------------------------------------------------------------------- stats */

/** One calendar day (UTC) of activity, from the server's `DayActivity`. */
export interface DayActivity {
  /** `YYYY-MM-DD`. */
  day: string;
  stepsDone: number;
  contentsDelivered: number;
  contentsCreated: number;
  /** What was billed that day, across every provider (US-079). */
  spendUsd: number;
}

/**
 * The window summed up, from the server's `Throughput` (US-079).
 *
 * `costPerDeliveredUsd` is the whole billed spend of the contents delivered in
 * the window — every call they ever made, not only the ones inside it —
 * divided by how many there were, and is `null` when nothing was delivered.
 * It is therefore **not** `spendUsd / delivered`, and the page may not print
 * the three as if one were the product of the others.
 */
export interface Throughput {
  days: number;
  /** The first day of the window, `YYYY-MM-DD`. */
  since: string;
  delivered: number;
  spendUsd: number;
  deliveredPerDay: number;
  spendPerDayUsd: number;
  costPerDeliveredUsd: number | null;
}

/**
 * Does version 1 ship? From the server's `OneShot` (one-shot ads US-037):
 * over the contents delivered in the window, each counted over its whole life;
 * `rulesLearned` is the house rules written inside the window.
 */
export interface OneShot {
  delivered: number;
  oneShot: number;
  /** 0–1; null when nothing was delivered. */
  rate: number | null;
  operatorRevisions: number;
  revisionsPerDelivered: number | null;
  rulesLearned: number;
  waits: number;
  waitsPerDelivered: number | null;
}

/**
 * The generic lines the bible let through, from the server's `BibleFigures`
 * (the ad bible US-033): over the contents delivered in the window whose plan
 * could be read, counted as `mc-admin eval` counts them.
 */
export interface BibleFigures {
  measured: number;
  genericLines: number;
  perDelivered: number | null;
  /** Newest approval first; `genericLines` is null without a plan to read. */
  contents: { id: string; name: string; genericLines: number | null }[];
}

/** Month-to-date spend with one provider, from the server's `ProviderSpend`. */
export interface ProviderSpend {
  provider: string;
  calls: number;
  costUsd: number;
}

/**
 * The pool as one tooltip: how many slots are in use, of how many, and what is
 * waiting behind them. The meter in the sidebar hangs this off `title`, so
 * hovering answers "why is nothing starting?".
 */
export function describeProductionSlots(productions: Stats['productions']): string {
  const lines = [
    `${String(productions.active)} of ${String(productions.max)} production slots in use`,
  ];
  if (productions.active === 0) lines.push('• nothing is in production');
  if (productions.queued > 0) {
    lines.push(`${String(productions.queued)} queued, in the order they were asked for`);
  }
  return lines.join('\n');
}

/** Mirrors the server's `StatsView`: everything the overview page shows. */
export interface Stats {
  generatedAt: string;
  contents: { total: number; byStatus: Record<ContentStatus, number> };
  steps: { total: number; done: number; inProgress: number; todo: number };
  /** Rendered versions across every content, all time. */
  versions: number;
  spend: { totalUsd: number; monthToDateUsd: number; byProvider: ProviderSpend[] };
  /** Contents waiting for a production slot right now. */
  queued: number;
  productions: {
    /** Production slots in use — what the cap actually counts. */
    active: number;
    queued: number;
    max: number;
    free: number;
  };
  hold: { until: string | null };
  /**
   * The month against the monthly cap (US-061). `capUsd` is null when the
   * operator has set no monthly ceiling, and `warned` is then false: there is
   * nothing to be warned about.
   */
  budget: {
    month: string;
    spentUsd: number;
    capUsd: number | null;
    warnThresholdPercent: number;
    warnAtUsd: number | null;
    warned: boolean;
  };
  /**
   * The machine the server runs on. `cpu` is the busy fraction (0–1) since the
   * previous poll, and is null on the first read after a restart, when there is
   * no earlier sample to measure against. Memory is in bytes.
   */
  host: { cpu: number | null; cores: number; memory: { used: number; total: number } };
  /**
   * What one content container is allowed of that machine (US-077) — the
   * `Memory` and `NanoCpus` ceilings the *next* container start applies, in
   * gigabytes and CPUs. On this poll rather than on a content's own view
   * because every container gets the same two numbers.
   */
  containers: { memoryGb: number; cpus: number };
  /**
   * Ideas worked out and waiting for a decision, batches queued or being
   * worked, and the newest batch holding an idea to decide (US-125).
   */
  ideas: { toDecide: number; working: number; newestBatchToDecide: string | null };
  /** Planned posts whose time has come, on non-archived contents. */
  posts: { due: number };
  /** Oldest first. */
  activity: DayActivity[];
  /** The same window as three figures, for the overview (US-079). */
  throughput: Throughput;
  /** How often version 1 ships, over the same window (one-shot ads US-037). */
  oneShot: OneShot;
  /** Generic lines per delivered content (the ad bible US-033); null when the server counts none. */
  bible: BibleFigures | null;
}

/** Polled by the overview and the app shell; aggregates over the database only. */
export async function fetchStats(signal?: AbortSignal, days = 14): Promise<Stats> {
  return api<Stats>(`/api/stats?days=${String(days)}`, signal ? { signal } : {});
}

/**
 * Mirrors the server's `ProductionView`: where the loop is, and what it is
 * measured against.
 *
 * The steps themselves come with {@link ContentPlan}, which the page already
 * polls; what is here is only what production adds — the counters, the timeout
 * the current step is held to, the queue and the hold.
 */
export interface Production {
  contentId: string;
  contentName: string;
  status: ContentStatus;
  /** True while this server is driving a loop for the content. */
  running: boolean;
  iteration: number;
  maxIterations: number;
  currentStepId: string | null;
  /** Failed attempts already spent on that step. */
  attempts: number;
  maxAttempts: number;
  steps: ProductionStep[];
  plan: PlanStatus;
  lastError: string | null;
  failureStage: ContentFailureStage | null;
  stepTimeoutMs: number;
  productionModel: string | null;
  startedAt: string | null;
  /** Waiting for a production slot rather than working. */
  queued: boolean;
  queuePosition: number | null;
  activeProductions: number;
  maxConcurrentProductions: number;
  /** Expiry of Claude's usage-limit hold while one is on. */
  holdUntil: string | null;
}

/** A step row as production has left it; the plan's own view is {@link PlanStep}. */
export interface ProductionStep {
  stepId: string;
  title: string;
  type: StepType;
  priority: number;
  status: StepStatus;
  attempts: number;
  durationMs: number | null;
  startedAt: string | null;
  finishedAt: string | null;
}

/** Polled by the content page while a production is running. */
export async function fetchProduction(id: string, signal?: AbortSignal): Promise<Production> {
  return api<Production>(
    `/api/contents/${encodeURIComponent(id)}/production`,
    signal ? { signal } : {},
  );
}

/**
 * **Start production**: the loop works through the steps in priority order.
 * Beyond the concurrency cap this is not a refusal — the content takes a place
 * in the queue, and the answer says so (`queued`, `queuePosition`).
 */
export async function startProduction(id: string): Promise<Production> {
  return api<Production>(`/api/contents/${encodeURIComponent(id)}/production/start`, {
    method: 'POST',
  });
}

/** **Stop production**: signals the agent and returns the content to `ready`. */
export async function stopProduction(id: string): Promise<Production> {
  return api<Production>(`/api/contents/${encodeURIComponent(id)}/production/stop`, {
    method: 'POST',
  });
}

/**
 * **Leave queue**: the content gives up its place under the concurrency cap and
 * goes back to plain `ready`. Not a stop — nothing is running — so the content
 * keeps everything it has, and **Start production** puts it at the back again.
 */
export async function leaveProductionQueue(id: string): Promise<Production> {
  return api<Production>(`/api/contents/${encodeURIComponent(id)}/production/leave-queue`, {
    method: 'POST',
  });
}

/* ------------------------------------------------------------------ retry */

/** Which entry point a retry goes through, mirroring the server's dispatch. */
export type RetryAction = 'setup' | 'production' | 'none';

/** The step a production retry picks up at: the first one that is not done. */
export interface RetryResumeStep {
  stepId: string;
  title: string;
  type: StepType;
  priority: number;
  status: StepStatus;
  attempts: number;
  maxAttempts: number;
}

/**
 * What **Retry** would do, worked out by the server before it is pressed.
 *
 * Every sentence in here is the server's: each one states a guarantee it makes
 * — that a lost container costs nothing, that a clone which succeeded is kept,
 * that the steps already done are left alone — so the page renders strings and
 * cannot disagree with what the button will actually do.
 */
export interface RetryPlan {
  contentId: string;
  contentName: string;
  status: ContentStatus;
  stage: ContentFailureStage | null;
  stageLabel: string;
  stageMeaning: string;
  /** `last_error`: what the server stored when it gave up. */
  reason: string | null;
  retryable: boolean;
  action: RetryAction;
  label: string;
  summary: string;
  /** What will happen, in order. */
  willDo: string[];
  freshContainer: boolean;
  resumeStep: RetryResumeStep | null;
  stepsDone: number;
  stepsTotal: number;
  /** Something to fix first; the retry still goes through. */
  warning: string | null;
  /** Why Retry is not offered. Null whenever `retryable`. */
  blocked: string | null;
}

/** What the retry answered: the plan it ran, and whether it started. */
export interface RetryOutcome {
  ok: boolean;
  plan: RetryPlan;
  message: string | null;
}

/** What a retry would do — the description the content page shows first. */
export async function fetchRetryPlan(id: string, signal?: AbortSignal): Promise<RetryPlan> {
  return api<RetryPlan>(`/api/contents/${encodeURIComponent(id)}/retry`, signal ? { signal } : {});
}

/** **Retry**: runs the plan the server just described. */
export async function retryContent(id: string): Promise<RetryOutcome> {
  return api<RetryOutcome>(`/api/contents/${encodeURIComponent(id)}/retry`, { method: 'POST' });
}

/* --------------------------------------------------------- the narration script */

/**
 * The narration as words, and the one decision in front of it (US-090).
 *
 * A `script` step writes them, the production stops, and this is what the
 * panel reads: the file in the workspace, not a copy of it — the voiceover
 * step speaks whatever is in that file.
 */
export interface ScriptView {
  readonly contentId: string;
  readonly stepId: string | null;
  readonly title: string | null;
  readonly path: string;
  readonly text: string;
  /** True while the production is stopped at this gate. */
  readonly awaitingApproval: boolean;
  /** True while the words at the gate are ones a sketch adjustment wrote (US-097). */
  readonly narrationChanged: boolean;
  readonly approvedAt: string | null;
  readonly words: number;
  /** Roughly how long it takes to say, at the pace the voice is set to. */
  readonly estimatedSeconds: number;
  /** The plan's `## Script changes` (US-016): the ideas-document lines the planner changed. */
  readonly scriptChanges: readonly ScriptChange[];
}

/** One ideas-document line the planner changed: "document → plan — why". */
export interface ScriptChange {
  readonly document: string;
  /** Empty when the line was dropped. */
  readonly plan: string;
  readonly why: string;
  readonly line: number;
}

export async function fetchScript(id: string, signal?: AbortSignal): Promise<ScriptView> {
  return api<ScriptView>(`/api/contents/${encodeURIComponent(id)}/script`, signal ? { signal } : {});
}

/** Saves an edit and decides nothing: the operator is still reading. */
export async function saveScript(id: string, text: string): Promise<ScriptView> {
  return api<ScriptView>(`/api/contents/${encodeURIComponent(id)}/script`, {
    method: 'PUT',
    body: JSON.stringify({ text }),
  });
}

/** **Approve**: save what is on screen, and let the voiceover speak it. */
export async function approveScript(id: string, text: string): Promise<ScriptView> {
  return api<ScriptView>(`/api/contents/${encodeURIComponent(id)}/script/approve`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}

/* ------------------------------------------------------------ the plan sketch */

/** Mirrors the server's `SKETCH_STATES`: where the sketch gate stands. */
export type SketchState = 'preparing' | 'adjusting' | 'ready' | 'failed';

/** Mirrors the server's `VISUAL_KINDS`: what a scene shows. */
export type VisualKind = 'capture' | 'image' | 'animation' | 'clip' | 'talk' | 'graphic';

/** An `animate` or `talk` step whose `**Placement:**` names a scene. */
export interface SketchPlacement {
  readonly stepId: string;
  readonly type: StepType;
  readonly title: string;
  /** Seconds into the scene, as the `@ n` of the placement is written. */
  readonly atSeconds: number;
}

/** How a scene’s `**Shot:**` line frames it, as the server parses it (`SHOT_FRAMINGS`). */
export type ShotFraming =
  | 'establishing'
  | 'wide'
  | 'two-shot'
  | 'over-the-shoulder'
  | 'medium'
  | 'close-up'
  | 'reaction'
  | 'insert'
  | 'point-of-view'
  | 'backdrop';

/** One `### Scene N` block of the storyboard in `content/plan.md`. */
export interface SketchScene {
  readonly number: number;
  readonly name: string;
  readonly seconds: number;
  /** The sum of the seconds of every scene before this one. */
  readonly startsAt: number;
  readonly text: readonly string[];
  readonly voiceover: readonly string[];
  readonly visual: { readonly kind: VisualKind; readonly description: string } | null;
  /** The framing of the scene’s `**Shot:**` line (US-009); `null` when it has none. */
  readonly framing: ShotFraming | null;
  readonly placements: readonly SketchPlacement[];
  /** What the last change did to this scene, against the copy kept for undo (US-107). */
  readonly change: SceneChange | null;
  /**
   * On voice timings (synthesis C3): the seconds the plan gave the scene before
   * the recorded take timed it. `null` on the plan's own timings.
   */
  readonly plannedSeconds: number | null;
  /** The seconds the take gave the scene; `null` on the plan's own timings. */
  readonly voicedSeconds: number | null;
}

/** Where the scene seconds come from: the recorded voice, or the plan's own budget. */
export type TimingSource = 'voice' | 'plan';

/** A recorded take the sketch panel plays. */
export interface SketchTake {
  /** The audio file, relative to the content. */
  readonly path: string;
  /** Who speaks in it, for a plan with several voices; `null` for the narration. */
  readonly speaker: string | null;
  /** Where the browser plays it from. */
  readonly url: string;
}

/** `new`: not in the plan before the last change; `changed`: different from it. */
export type SceneChange = 'new' | 'changed';

/** Which timing rule a problem broke (US-104). */
export type SketchProblemKind =
  | 'scene_too_short'
  | 'scene_too_long'
  | 'text_too_long'
  | 'narration_too_fast'
  | 'cuts_too_close'
  | 'cuts_too_frequent'
  | 'total_mismatch'
  | 'total_too_long'
  | 'scene_count';

/** A storyboard error; `sceneNumber` is `null` for one about the whole plan. */
export interface SketchProblem {
  readonly sceneNumber: number | null;
  readonly kind: SketchProblemKind;
  /** The technical reading, for "Show details". */
  readonly message: string;
  /** One plain sentence. */
  readonly plain: string;
  /** The request "Fix this for me" sends to the adjust pass. */
  readonly fixPrompt: string;
}

/** Mirrors the server's `SketchView` (US-092): the storyboard as scene cards. */
export interface SketchView {
  readonly state: SketchState | null;
  readonly error: string | null;
  readonly approvedAt: string | null;
  readonly totalSeconds: number;
  readonly scenes: readonly SketchScene[];
  readonly problems: readonly SketchProblem[];
  readonly lastChange: string | null;
  /** Whether **Undo last change** is possible now (US-112). */
  readonly canUndo: boolean;
  /** The scenes the last change took out (US-107); empty without a kept copy. */
  readonly removed: readonly { readonly number: number; readonly name: string }[];
  /** The total length before the last change; `null` without a kept copy. */
  readonly previousTotalSeconds: number | null;
  /**
   * The fit or adjustment running now, or the one that just failed (US-110):
   * when it started and the operator's request (`null` for a fit). `null`
   * otherwise, and after a server restart.
   */
  readonly run: { readonly startedAt: string; readonly request: string | null } | null;
  /** Where the scene seconds come from (synthesis C3). */
  readonly timingSource: TimingSource;
  /** Why the seconds are the plan's, in one plain sentence; `null` when they follow the voice. */
  readonly timingReason: string | null;
  /** The voice moved the length far from the plan, in plain words; `null` otherwise. */
  readonly timingWarning: string | null;
  /** The length the plan was written to, before the voice timed it; `null` on the plan's timings. */
  readonly plannedTotalSeconds: number | null;
  /** The recorded takes, to be heard at the gate. */
  readonly takes: readonly SketchTake[];
}

/** The sketch as it stands (`GET /api/contents/:id/sketch`). */
export async function fetchSketch(id: string, signal?: AbortSignal): Promise<SketchView> {
  return api<SketchView>(`/api/contents/${encodeURIComponent(id)}/sketch`, signal ? { signal } : {});
}

/** **Approve scenes**: the scenes are agreed, production goes on. */
export async function approveSketch(id: string): Promise<SketchView> {
  return api<SketchView>(`/api/contents/${encodeURIComponent(id)}/sketch/approve`, {
    method: 'POST',
  });
}

/**
 * **Change with a request** (US-096): one headless pass changes the scenes as the
 * operator asked. Answers as soon as the pass has started — `state` is
 * `adjusting`, or already `failed` when it could not start at all.
 */
export async function adjustSketch(id: string, prompt: string): Promise<SketchView> {
  return api<SketchView>(`/api/contents/${encodeURIComponent(id)}/sketch/adjust`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
}

/** What the operator typed into a scene card in edit mode (US-100). */
export interface SketchSceneEdit {
  readonly name: string;
  readonly seconds: number;
  readonly text: readonly string[];
}

/**
 * **Save** a hand edit of one scene (US-099): the plan is rewritten at once,
 * no agent involved, and the answer is the whole sketch read back.
 */
export async function editSketchScene(
  id: string,
  sceneNumber: number,
  edit: SketchSceneEdit,
): Promise<SketchView> {
  return api<SketchView>(
    `/api/contents/${encodeURIComponent(id)}/sketch/scenes/${String(sceneNumber)}`,
    { method: 'PUT', body: JSON.stringify(edit) },
  );
}

/** **Undo last change** (US-112): the plan as it was before the last change. */
export async function undoSketch(id: string): Promise<SketchView> {
  return api<SketchView>(`/api/contents/${encodeURIComponent(id)}/sketch/undo`, { method: 'POST' });
}

/** **Try again** after a failed fit or adjustment (US-095). */
export async function prepareSketch(id: string): Promise<SketchView> {
  return api<SketchView>(`/api/contents/${encodeURIComponent(id)}/sketch/prepare`, {
    method: 'POST',
  });
}

/* ------------------------------------------------------------ creative notes */

/**
 * The checks no score outweighs (synthesis C1): any one fails the critique.
 * A note for the operator; it never holds **Approve scenes**.
 */
export type CritiqueHardFailCode =
  | 'no_hook'
  | 'product_unclear_5s'
  | 'no_proof'
  | 'no_ask'
  | 'unsourced_claim'
  | 'cold_reader_wrong'
  | 'words_mismatch'
  // The characters in the brief speak and the plan gives them no voice.
  | 'dialogue_unvoiced'
  // For the draft video (synthesis C8).
  | 'hook_frame'
  | 'unreadable'
  | 'slideshow'
  | 'motion_broken'
  | 'look_unfinished'
  | 'out_of_sync'
  | 'sound_broken'
  | 'frozen_people'
  // For the staging (who stands where, and how the camera covers them).
  | 'no_establishing'
  | 'isolated_portraits'
  | 'crossed_line'
  | 'no_staging'
  | 'prop_as_place'
  | 'poor_coverage'
  | 'continuity_broken'
  // A house rule of the brand book broken, the rule quoted (one-shot ads US-012).
  | 'house_rule_broken'
  // The story as a first-time viewer follows it, and a line too short to hear (one-shot ads US-017).
  | 'line_unmotivated'
  | 'payoff_without_setup'
  | 'pronoun_without_noun'
  | 'line_too_short'
  // A line an agent wrote breaks the banned list (the ad bible US-011).
  | 'banned_phrase'
  | 'claim_unbacked'
  // A still of a cast character without their reference (the ad bible US-016).
  | 'cast_reference_missing'
  // A hook an agent wrote that repeats an earlier hook of the brand (the ad bible US-020).
  | 'hook_repeated'
  // The story, the joke and what it is, each with a quoted line (the ad bible US-022).
  | 'story_broken'
  | 'joke_broken'
  | 'what_it_is_unclear'
  // The swap test, the insider voice and the claims checks (the ad bible US-023).
  | 'swap_test_failed'
  | 'outsider_word'
  | 'fiscal_detail_wrong'
  | 'claim_adjective'
  | 'claim_without_limit'
  | 'claim_contradicts'
  | 'replaces_person'
  | 'fear_without_action'
  // A person shown with no `looking_at`, or an untagged look into the lens (the ad bible US-028).
  | 'gaze_missing'
  | 'gaze_to_camera_untagged';

/** What a critique judged: the words, the draft video before its final render, or the staging. */
export type CritiqueKind = 'script' | 'draft' | 'staging';

/** The draft a critique watched, and whether it sent the composition back. */
export interface CritiqueDraftFacts {
  stepId: string | null;
  path: string;
  /** What the critic was shown: the video, its frames, or nothing (the checks alone). */
  watched: 'video' | 'frames' | 'none';
  /** True when its hard fails sent the compose step back for one more pass. */
  sentBack: boolean;
  measured: string[];
}

export interface CritiqueHardFail {
  code: CritiqueHardFailCode;
  message: string;
  where?: string;
}

export interface CritiqueLintFinding {
  code: string;
  severity: 'block' | 'warn';
  message: string;
  where?: string;
}

/** What a stranger who knew nothing about the product took from one track of the ad. */
export interface CritiqueColdReading {
  product: string;
  whatItDoes: string;
  benefit: string;
  ask: string;
  /** `null` when the track was not read, or no model could judge it. */
  correct: boolean | null;
}

export type CritiqueColdReader = 'voiceOnly' | 'textOnly' | 'firstFiveSeconds';

export interface CritiqueRewrite {
  where: string;
  before: string;
  after: string;
  why: string;
}

/** A critique exactly as `mc critique script` prints it. */
export interface CritiqueResult {
  id: string;
  kind: CritiqueKind;
  inputHash: string;
  verdict: 'pass' | 'fail';
  hardFails: CritiqueHardFail[];
  /** 0–3 per criterion; empty when only the deterministic checks ran. */
  scores: Partial<Record<string, { score: 0 | 1 | 2 | 3; note: string }>>;
  coldReader: Record<CritiqueColdReader, CritiqueColdReading>;
  lint: CritiqueLintFinding[];
  rewrites: CritiqueRewrite[];
  summary: string;
  model: string;
  /** Present on a critique of the draft video. */
  draft?: CritiqueDraftFacts;
}

/** One stored critique. */
export interface CritiqueRecord {
  id: string;
  kind: CritiqueKind;
  inputHash: string;
  status: 'done' | 'failed';
  verdict: 'pass' | 'fail' | null;
  hardFails: number;
  model: string | null;
  costUsd: number;
  createdAt: string;
  error: string | null;
  /** True when it judged the words the plan says right now. */
  current: boolean;
  result: CritiqueResult | null;
}

/** "Creative notes" for one content. */
export interface CritiquesView {
  contentId: string;
  kind: CritiqueKind;
  available: boolean;
  engine: string | null;
  note: string | null;
  currentHash: string | null;
  problem: string | null;
  running: { startedAt: string } | null;
  current: CritiqueRecord | null;
  latest: CritiqueRecord | null;
  history: Omit<CritiqueRecord, 'result'>[];
}

export async function fetchCritiques(
  id: string,
  signal?: AbortSignal,
  kind: CritiqueKind = 'script',
): Promise<CritiquesView> {
  return api<CritiquesView>(
    `/api/contents/${encodeURIComponent(id)}/critiques?kind=${kind}`,
    signal ? { signal } : {},
  );
}

/** **Run again**: a fresh critique of the words (or the draft) as they stand; answers with it running. */
export async function runCritique(id: string, kind: CritiqueKind = 'script'): Promise<CritiquesView> {
  return api<CritiquesView>(`/api/contents/${encodeURIComponent(id)}/critiques/run`, {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
}

/* ------------------------------------------------------ versions and the player */

/** What part a file plays in a version. */
export type VersionFileRole = 'render' | 'poster' | 'captions' | 'variant' | 'contact-sheet';

/** One file of one version, with everything the player needs to show it. */
export interface VersionFile {
  role: VersionFileRole;
  /** The format preset it was rendered at; `null` when nothing says. */
  preset: string | null;
  /** Where it lives in the workspace, relative to `content/`. */
  path: string;
  name: string;
  mime: string;
  bytes: number;
  /**
   * The object in the bucket this file was mirrored to (US-054). The page asks
   * {@link fetchAssetUrl} for a short-lived URL to load it from; `null` means
   * it has not reached the bucket yet, and nothing can play it.
   */
  assetId: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
}

/** One rendered version of a content. */
export interface Version {
  number: number;
  createdAt: string;
  current: boolean;
  render: VersionFile | null;
  poster: VersionFile | null;
  /** The SRT sidecar, served as WebVTT so a `<track>` can show it. */
  captions: VersionFile | null;
  variants: VersionFile[];
  /** The review pass's markdown (US-043). */
  reviewMd: string | null;
  /**
   * The share copy that ships with this version (US-075), already split by the
   * server into the blocks the page puts a copy button on. `null` when the
   * `copy` step has not produced anything for this version.
   */
  shareCopy: ShareCopy | null;
  /**
   * One image of the whole render (US-080). `null` for a still, and for a
   * version whose sheet was never drawn.
   */
  contactSheet: ContactSheet | null;
}

/**
 * The grid one contact sheet was drawn with, as the server recorded it when
 * ffmpeg drew it (US-080).
 *
 * Every number the page needs to put a button over each frame and to turn that
 * button into a timestamp. It is read rather than derived on purpose: the tile
 * size and the column count are the ones *this* sheet was made with, and a
 * page that assumed today's constants would seek to the wrong second on every
 * sheet drawn before they last changed.
 */
export interface ContactSheetLayout {
  path: string;
  /** How many tiles were drawn; the last row may be short. */
  frames: number;
  columns: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  /** The gap between tiles, and around the sheet, in pixels. */
  gap: number;
  /** The whole image, in pixels. */
  width: number;
  height: number;
  /** Seconds between two tiles: 1, until a render is long enough to widen it. */
  intervalSeconds: number;
  durationMs: number;
  /** The tiles a scene starts on, ascending; the first is always 0. */
  scenes: number[];
}

/** The sheet of one version: the file, and the grid it was drawn with. */
export interface ContactSheet {
  file: VersionFile;
  layout: ContactSheetLayout;
}

/**
 * Where the sheet's bytes are (US-080).
 *
 * Not a presigned URL like every other file of a version: the sheet is a small
 * JPEG served by this server behind the session cookie, so it draws on the
 * first paint of the page rather than after a round trip for a signature, and
 * on an install whose bucket the browser cannot reach at all.
 */
export function contactSheetUrl(contentId: string, version: number): string {
  return `/api/contents/${encodeURIComponent(contentId)}/versions/${String(version)}/contact-sheet`;
}

/** The second of the render one tile of a sheet shows. */
export function contactSheetTileSeconds(layout: ContactSheetLayout, tile: number): number {
  return Math.max(0, Math.trunc(tile)) * layout.intervalSeconds;
}

/**
 * Where one tile sits on the sheet, as percentages of the image.
 *
 * Percentages rather than pixels because the sheet is drawn at whatever width
 * the panel gives it: the overlay has to scale with the picture, and the grid
 * is the server's arithmetic either way (`tileOrigin` in
 * `server/src/production/contact-sheet.ts`).
 */
export function contactSheetTileBox(
  layout: ContactSheetLayout,
  tile: number,
): { left: string; top: string; width: string; height: string } {
  const column = tile % layout.columns;
  const row = Math.floor(tile / layout.columns);
  const x = layout.gap + column * (layout.tileWidth + layout.gap);
  const y = layout.gap + row * (layout.tileHeight + layout.gap);
  return {
    left: `${String((x / layout.width) * 100)}%`,
    top: `${String((y / layout.height) * 100)}%`,
    width: `${String((layout.tileWidth / layout.width) * 100)}%`,
    height: `${String((layout.tileHeight / layout.height) * 100)}%`,
  };
}

/** The platforms `mc-copy` writes a post for; the ids the server's blocks carry. */
export type SharePlatformId = 'linkedin' | 'instagram' | 'facebook' | 'tiktok' | 'youtube' | 'x';

/**
 * The platforms the page knows — the same order and the same labels as
 * `SHARE_PLATFORMS` in `server/src/versions/share-copy.ts` (synthesis S9).
 *
 * A `copy` step writes the placement's post first (LinkedIn when the plan names
 * none) and then only the platforms the plan points to, so the page lists the
 * posts in the order they were written; the default platform gets a row even
 * when nothing was written for it, so that missing post is visibly missing.
 */
export const SHARE_PLATFORMS: readonly { id: SharePlatformId; label: string }[] = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'x', label: 'X' },
];

/**
 * Where a post can go: `SHARE_PLATFORMS`, then `other` (which needs a label).
 * The server's `POST_PLATFORMS` in `server/src/db/posts.ts`; web-mirror.test.ts holds them together.
 */
export const POST_PLATFORMS: readonly { id: PostPlatform; label: string }[] = [
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'x', label: 'X' },
  { id: 'other', label: 'Other' },
];

/** The platform written for when the plan names no placement. */
export const DEFAULT_SHARE_PLATFORM: SharePlatformId = 'linkedin';

/** One platform's block of `share-copy.txt`, ready to paste. */
export interface SharePost {
  /** The platform the block's heading named, or `null` for an unknown one. */
  platform: SharePlatformId | null;
  /** What the button says: the platform's name, or the heading as written. */
  label: string;
  body: string;
}

/** The share copy of one version, as the content page reads it. */
export interface ShareCopy {
  /** `share-copy.txt` whole, which is what **Copy all** puts on the clipboard. */
  text: string;
  /** `share-copy-variants.md`: the alternatives, or `null` when there are none. */
  variants: string | null;
  posts: SharePost[];
  /** Credits a generated clip obliges, and whether the copy pays them. */
  credits: ShareCopyCredit[];
}

/**
 * One licence line this content's clips oblige (US-087).
 *
 * A Runway model requires "Powered by Runway" wherever its output is
 * published, which is a condition on the *post* rather than a fact about the
 * file — so it is checked against what the `copy` step wrote and shown beside
 * the posts, before the operator pastes one.
 */
export interface ShareCopyCredit {
  line: string;
  source: string;
  note: string;
  satisfied: boolean;
}

/** Every version of one content, newest first. */
export interface Versions {
  contentId: string;
  kind: ContentKind;
  currentVersion: number;
  versions: Version[];
}

/** The versions of a content and the files behind them, polled by its page. */
export async function fetchVersions(id: string, signal?: AbortSignal): Promise<Versions> {
  return api<Versions>(
    `/api/contents/${encodeURIComponent(id)}/versions`,
    signal ? { signal } : {},
  );
}

/* -------------------------------------------------------- delivered work */

/**
 * One delivered content, as a picture (US-074).
 *
 * The strip on the Overview and the thumbnails of the content grid read the
 * same list: what has been approved, newest first, each with the one file that
 * can be drawn for it. `poster` is `null` for a content whose render has not
 * reached the bucket — there is nothing to load it from, and a tile then says
 * so in words.
 */
export interface Delivery {
  contentId: string;
  name: string;
  kind: ContentKind;
  versionNumber: number;
  deliveredAt: string;
  poster: VersionFile | null;
}

/** The delivered contents, newest first. */
export interface Deliveries {
  deliveries: Delivery[];
}

/**
 * What has been delivered lately.
 *
 * Read on its own rather than folded into `GET /api/contents`: the strip is
 * about versions and their files, which the content list knows nothing about,
 * and it changes when something is approved rather than every three seconds.
 */
export async function fetchDeliveries(limit?: number, signal?: AbortSignal): Promise<Deliveries> {
  const query = limit === undefined ? '' : `?limit=${String(limit)}`;
  return api<Deliveries>(`/api/deliveries${query}`, signal ? { signal } : {});
}

/* --------------------------------------------------------- asset delivery */

/** How the browser is to fetch an asset (US-055). */
export type DeliverySource = 'presigned' | 'public' | 'proxy';

/** A short-lived URL for one asset, as the server just issued it. */
export interface AssetDelivery {
  assetId: string;
  /**
   * What to put in a `src` or an `href`. A presigned S3 URL, a URL under the
   * bucket's public base URL, or — for a file this server has to convert, and
   * for a bucket whose CORS cannot be set — {@link AssetDelivery.rawUrl}.
   */
  url: string;
  source: DeliverySource;
  /** When the signature stops working; `null` for a URL that does not expire. */
  expiresAt: string | null;
  expiresInSeconds: number | null;
  mime: string;
  name: string;
  /** The proxy through this server, which always works whatever the CORS says. */
  rawUrl: string;
}

/**
 * Where to fetch one asset from, for the next fifteen minutes.
 *
 * Asked for per asset rather than baked into the versions listing: a URL in a
 * polled payload would be re-signed every three seconds, and a `<video>` whose
 * `src` changes under it stops playing.
 */
export async function fetchAssetUrl(
  contentId: string,
  assetId: string,
  signal?: AbortSignal,
): Promise<AssetDelivery> {
  return api<AssetDelivery>(
    `/api/contents/${encodeURIComponent(contentId)}/assets/${encodeURIComponent(assetId)}/url`,
    signal ? { signal } : {},
  );
}

/* ---------------------------------------------------------- the asset gallery */

/** What a file is, which is what picks the thumbnail drawn for it (US-056). */
export type AssetKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'captions'
  | 'document'
  | 'source'
  | 'render'
  | 'poster';

/** The filter value that means "the ones with no step" / "no version". */
export const GALLERY_NONE = 'none';

/** The provider call that produced a file, when one did. */
export interface GalleryProvider {
  name: string;
  operation: string;
  model: string | null;
  costUsd: number;
}

/** The step a file is attributed to. */
export interface GalleryStepRef {
  stepId: string;
  title: string;
  type: StepType;
}

/** One file of a content, as one tile of the gallery. */
/**
 * What a generated clip was generated from (US-086).
 *
 * A tile that has one may be re-rolled on another model; a tile that does not
 * — stock, a capture, anything a hand put there — has no prompt to send again,
 * and offers nothing rather than asking the operator to invent one.
 */
export interface GalleryClip {
  prompt: string;
  model: string;
  seconds: number;
  resolution: string | null;
  aspectRatio: string | null;
  reference: string | null;
  /** The take it was re-rolled from, when it is a re-roll of one. */
  rerollOf: string | null;
}

/** What `mc animate verdict` can have recorded about an animation. */
export type GalleryAnimationVerdict = 'unchecked' | 'pass' | 'fail' | 'fallback';

/** What an animation was animated from, and how its check went (US-011). */
export interface GalleryAnimation {
  /** The image that was animated, relative to `content/`. */
  source: string;
  /** The row the source thumbnail is drawn from; `null` when it is not in the tree. */
  sourceAssetId: string | null;
  motion: string;
  model: string;
  seconds: number | null;
  /** Attempts used: the highest attempt number recorded. */
  attempts: number;
  /** Every attempt at this path, together. */
  costUsd: number;
  verdict: GalleryAnimationVerdict;
  reason: string | null;
  /** The check's contact sheet, relative to `content/`. */
  sheet: string;
  sheetAssetId: string | null;
  fallbackStill: string | null;
}

export interface GalleryAsset {
  id: string;
  /** Relative to `content/` — the path the step wrote. */
  path: string;
  name: string;
  directory: string;
  kind: AssetKind;
  mime: string;
  bytes: number | null;
  sha256: string | null;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  loudnessLufs: number | null;
  fps: number | null;
  step: GalleryStepRef | null;
  version: number | null;
  provider: GalleryProvider | null;
  /** The ask a generated clip came from; `null` for every other file. */
  clip: GalleryClip | null;
  /** What an animation was made from; `null` for everything `mc animate` did not make. */
  animation: GalleryAnimation | null;
  licence: string | null;
  attribution: string | null;
  /** False while the file is in the workspace but not yet in the bucket. */
  uploaded: boolean;
  uploadedAt: string | null;
  createdAt: string;
  /** How many sets of bytes this path has had; 1 unless it was rewritten. */
  revisions: number;
  /** The same-origin proxy, which a text preview is read through. */
  rawUrl: string;
}

/** One option of the step filter; `stepId` is {@link GALLERY_NONE} for the loose files. */
export interface GalleryStepFilter {
  stepId: string;
  title: string;
  type: StepType | null;
  count: number;
}

/** One option of the version filter; `null` is the material every version shares. */
export interface GalleryVersionFilter {
  version: number | null;
  count: number;
}

/** Every file of a content, with the vocabulary its filters offer. */
export interface Gallery {
  contentId: string;
  assets: GalleryAsset[];
  steps: GalleryStepFilter[];
  versions: GalleryVersionFilter[];
  /** How many tiles the content has in all, whatever the filter shows. */
  total: number;
}

/** What the gallery is narrowed to; `null` is "everything". */
export interface GalleryQuery {
  step: string | null;
  version: string | null;
}

/**
 * The files of one content, filtered by the server.
 *
 * The filtering is the server's so that the answer is always a whole one: the
 * counts beside each option are of the whole content, and the list is exactly
 * what the chosen filter matches — never a subset the browser trimmed.
 */
export async function fetchGallery(
  id: string,
  query: GalleryQuery,
  signal?: AbortSignal,
): Promise<Gallery> {
  const params = new URLSearchParams();
  if (query.step !== null) params.set('step', query.step);
  if (query.version !== null) params.set('version', query.version);
  const search = params.toString();
  return api<Gallery>(
    `/api/contents/${encodeURIComponent(id)}/assets${search === '' ? '' : `?${search}`}`,
    signal ? { signal } : {},
  );
}

/* --------------------------------------------- the review pass and approval */

/** How bad a finding is; the server's own vocabulary, in listing order. */
export const REVIEW_SEVERITIES = ['block', 'warning', 'note'] as const;
export type ReviewSeverity = (typeof REVIEW_SEVERITIES)[number];

/**
 * The layer that owns a defect (synthesis S6). A round fixes it there, for the
 * whole class; one in `words` is the operator's to decide and never a round's.
 */
export type ReviewLayer = 'words' | 'storyboard' | 'compose' | 'audio' | 'capture' | 'render';

/** One defect the review pass found (US-043). */
export interface ReviewFinding {
  id: string;
  severity: ReviewSeverity;
  /** `00:12.4` into the render; `null` when nothing timed it. */
  timestamp: string | null;
  scene: string | null;
  /** The score or layout question it came off: `hook`, `safe area`, `audio`, … */
  area: string;
  summary: string;
  /** What to change; `null` for an observation with nothing to do. */
  detail: string | null;
  /** `null` on a review stored before layers existed. */
  layer: ReviewLayer | null;
  /** One place, or every place the same cause occurs; `null` on an older review. */
  scope: 'instance' | 'class' | null;
}

/** What a render is scored on, once, 0–3 (synthesis S6), in the order the page lists them. */
export const REVIEW_SCORE_AXES = [
  'hook',
  'clarity',
  'brand',
  'proof',
  'ask',
  'sync',
  'motion',
  'look',
  'finish',
] as const;
export type ReviewScoreAxis = (typeof REVIEW_SCORE_AXES)[number];

/** One axis's score and the moment that earned it. */
export interface ReviewScore {
  score: 0 | 1 | 2 | 3;
  note: string;
}

/** What the server measured before the review looked, as the page shows it. */
export interface ReviewEvidenceSummary {
  lines: string[];
  warnings: string[];
  gaps: string[];
}

/** One stored review of one version. */
export interface ReviewReport {
  version: number;
  shippable: boolean;
  verdict: string;
  /** Empty on a review stored before scores existed. */
  scores: Partial<Record<ReviewScoreAxis, ReviewScore>>;
  findings: ReviewFinding[];
  checked: string[];
  framesInspected: number;
  reviewedAt: string;
  model: string | null;
  evidence?: ReviewEvidenceSummary;
}

export type ReviewCounts = Record<ReviewSeverity, number>;

/** What the content page shows beside **Approve**. */
export interface ReviewView {
  contentId: string;
  contentName: string;
  status: ContentStatus;
  version: number;
  report: ReviewReport | null;
  reviewMd: string | null;
  counts: ReviewCounts;
  /** Blocking findings a round can fix; those about the approved words are in `suggestions`. */
  blocking: number;
  /** Findings about the words you approved: suggestions, never acted on by a round. */
  suggestions: ReviewFinding[];
  /** What the probes noticed without refusing a step, in plain words. */
  probeWarnings: string[];
  canReview: boolean;
  canApprove: boolean;
  approvedAt: string | null;
  /** Every revision asked for so far, newest first (US-044). */
  revisions: RevisionEntry[];
  /** The automatic revision rounds: what is running, and what each one fixed. */
  auto: AutoReviewView;
  /** Why neither is offered, in the server's words; `null` when one is. */
  blocked: string | null;
}

/** What an automatic round is doing right now (US-048). */
export type AutoReviewPhase = 'reviewing' | 'revising' | 'producing';

/** One automatic round, as the page reports it afterwards. */
export interface ReviewRound {
  round: number;
  /** The version the round was started from, and whose findings it fixed. */
  version: number;
  producedVersion: number | null;
  worked: ReviewFinding[];
  fixed: ReviewFinding[];
  remaining: ReviewFinding[];
  introduced: ReviewFinding[];
  /** False while the version it produced has not been reviewed yet. */
  settled: boolean;
  summary: string;
}

/** The live state of the automatic rounds, polled with the review. */
export interface AutoReviewView {
  running: boolean;
  round: number;
  limit: number;
  roundsLeft: number;
  phase: AutoReviewPhase | null;
  /** The step in flight, in the server's words. */
  phaseLabel: string | null;
  working: ReviewFinding[];
  step: { stepId: string; title: string } | null;
  stopped: boolean;
  stoppedAt: string | null;
  canShowNow: boolean;
  rounds: ReviewRound[];
  summary: string;
}

/** What **Show me now** answered with. */
export interface ShowNowResult {
  contentId: string;
  ok: boolean;
  /** Whether the content is in review now, or when the work in flight lands. */
  movedToReview: boolean;
  version: number;
  message: string;
  auto: AutoReviewView;
}

/** One entry of the revision history: what was asked, and what it produced. */
export interface RevisionEntry {
  /** Who asked: the operator, or an automatic round. */
  source: 'operator' | 'review';
  /** The version the feedback was recorded against, and rejected. */
  version: number;
  feedback: string;
  /** The version the revision produced; `null` while it is being made. */
  producedVersion: number | null;
  producedAt: string | null;
  /** `running` while the plan is being revised, `failed` when it could not be; `null` once production took it. */
  state: 'running' | 'failed' | null;
  /** Why it failed; `null` unless `state` is `failed`. */
  message: string | null;
  startedAt: string | null;
}

/** What **Request changes** answers at once: the pass is running, and the review view shows how it ends. */
export interface RevisionStarted {
  ok: true;
  started: true;
  contentId: string;
  version: number;
  nextVersion: number;
  feedback: string;
  source: 'operator' | 'review';
  message: string;
  history: RevisionEntry[];
}

/** A rule the revision pass broke, which is why its plan was not applied. */
export interface RevisionProblem {
  code: string;
  stepId: string;
  message: string;
}

/**
 * The outcome of running a pass. A pass that could not run is `ok: false` with
 * a sentence, never an error: it is a state the panel shows.
 */
export interface ReviewRunResult {
  ok: boolean;
  message: string;
  view: ReviewView;
}

/** What approving answered with; `review` is the page's new state. */
export interface ApprovalResult {
  contentId: string;
  version: number;
  approvedAt: string;
  containerRemoved: boolean;
  workspaceKept: true;
  review: ReviewView | null;
}

/** What requesting changes answered with: the next version, or why not. */
export interface RevisionResult {
  /** Whether the plan was amended and production restarted. */
  ok: boolean;
  contentId: string;
  version: number;
  /** The version this revision is producing: `version + 1`. */
  nextVersion: number;
  feedback: string;
  source: 'operator' | 'review';
  blocking: ReviewFinding[];
  status: ContentStatus;
  /** Step ids the pass added, in plan order. */
  appended: string[];
  /** Rules the amended plan broke; the plan was put back as it was. */
  problems: RevisionProblem[];
  /** Why the amended plan does not parse, when that is what went wrong. */
  parseErrors: { line: number; message: string }[];
  message: string;
  history: RevisionEntry[];
}

/** The stored review of the current version, polled by the content page. */
export async function fetchReview(id: string, signal?: AbortSignal): Promise<ReviewView> {
  return api<ReviewView>(`/api/contents/${encodeURIComponent(id)}/review`, signal ? { signal } : {});
}

/** Runs one review pass inside the content container. */
export async function runReview(id: string): Promise<ReviewRunResult> {
  return api<ReviewRunResult>(`/api/contents/${encodeURIComponent(id)}/review`, {
    method: 'POST',
  });
}

/**
 * **Show me now**: stop the automatic rounds and show this version.
 *
 * Never discards what is running — the pass in flight finishes and its version
 * is the one shown — so the answer says whether the content is in review now or
 * the moment that work lands.
 */
export async function showMeNow(id: string): Promise<ShowNowResult> {
  return api<ShowNowResult>(`/api/contents/${encodeURIComponent(id)}/show-now`, {
    method: 'POST',
  });
}

/** **Approve**: the content is delivered, its container goes, its workspace stays. */
export async function approveContent(id: string): Promise<ApprovalResult> {
  return api<ApprovalResult>(`/api/contents/${encodeURIComponent(id)}/approve`, {
    method: 'POST',
  });
}

/**
 * **Request changes**: one headless pass edits the plan with the feedback and
 * the findings, and the next version is produced from it. Answers as soon as
 * the pass has started; the review view says how it ended.
 */
export async function requestChanges(id: string, feedback: string): Promise<RevisionStarted> {
  return api<RevisionStarted>(`/api/contents/${encodeURIComponent(id)}/revise`, {
    method: 'POST',
    body: JSON.stringify({ feedback }),
  });
}

/* ---------------------------------------------------------------- providers */

/** One choice on a picker the panel filled in from the provider's own API. */
export interface ProviderFieldOption {
  value: string;
  label: string;
  detail: string | null;
}

/** One of a provider's defaults, with its current value and its choices. */
export interface ProviderFieldView {
  key: string;
  label: string;
  help: string;
  fallback: string;
  value: string;
  options: ProviderFieldOption[];
}

export type ProviderCheckState = 'passed' | 'failed' | 'skipped';

export interface ProviderCheck {
  name: string;
  state: ProviderCheckState;
  detail: string;
  /** The provider's own error text, kept verbatim. */
  error: string | null;
}

export interface ProviderValidation {
  ok: boolean;
  at: string;
  message: string;
  checks: ProviderCheck[];
}

/** What this install's own ledger says it drew from the pool this cycle. */
export interface GatewayCreditEstimate {
  voiceCredits: number;
  musicCredits: number;
  effectCredits: number;
  /** Images, where a provider sells those; absent for one that does not. */
  imageCredits?: number;
  totalCredits: number;
  calls: number;
}

/**
 * A provider's credit balance. One pool: voice, music and effects all come out
 * of the same figure and the gateway enforces no split between them.
 */
export interface ProviderCredits {
  at: string;
  tier: string | null;
  usedCredits: number | null;
  limitCredits: number | null;
  remainingCredits: number | null;
  resetsAt: string | null;
  cycleStartsAt: string | null;
  gatewayEstimate: GatewayCreditEstimate;
  note: string;
}

/** Mirrors `ProviderSettings`: the key is write-only, its last four are not. */
export interface ProviderSettingsView {
  id: string;
  enabled: boolean;
  keyLastFour: string | null;
  keyConfigured: boolean;
  defaults: Record<string, string>;
  /** Switched on *and* keyed: a call would be attempted rather than refused. */
  usable: boolean;
}

/** What the provider last said, so the panel opens on it. */
export interface ProviderSnapshot {
  validation: ProviderValidation | null;
  credits: ProviderCredits | null;
  options: Record<string, ProviderFieldOption[]>;
}

export interface ProviderPrice {
  type: string;
  operation: string;
  model: string | null;
  unit: string;
  usdPerUnit: number;
  unitsPerStep: number;
  basis: string;
}

export interface ProviderView {
  id: string;
  label: string;
  summary: string;
  keyUrl: string;
  keyHelp: string;
  stepTypes: string[];
  fields: ProviderFieldView[];
  operations: string[];
  prices: ProviderPrice[];
  settings: ProviderSettingsView;
  snapshot: ProviderSnapshot;
}

/** A partial write: omitted keeps, `null` clears. */
export interface ProviderUpdate {
  enabled?: boolean;
  /** Write-only. Sent only when one was typed; `null` clears the stored key. */
  apiKey?: string | null;
  defaults?: Record<string, string | null>;
}

export async function fetchProviders(signal?: AbortSignal): Promise<ProviderView[]> {
  const view = await api<{ providers: ProviderView[] }>(
    '/api/settings/providers',
    signal ? { signal } : {},
  );
  return view.providers;
}

export async function saveProvider(id: string, update: ProviderUpdate): Promise<ProviderView> {
  const saved = await api<{ provider: ProviderView }>(`/api/settings/providers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(update),
  });
  return saved.provider;
}

/**
 * Runs the provider's own named checks and, when they pass, refreshes the
 * pickers and the credit balance. A provider that refuses the key is a `200`
 * with `ok: false` and its own error on the check that failed — the checks are
 * the answer, not the HTTP status.
 */
export async function validateProvider(
  id: string,
): Promise<{ validation: ProviderValidation; provider: ProviderView }> {
  return api<{ validation: ProviderValidation; provider: ProviderView }>(
    `/api/settings/providers/${id}/validate`,
    { method: 'POST' },
  );
}

/* --------------------------------- re-rolling a clip (US-086) */

/** What a re-generation produced, as the gallery reports it. */
export interface ClipReroll {
  contentId: string;
  sourceAssetId: string;
  assetId: string | null;
  path: string;
  model: string;
  prompt: string;
  seconds: number;
  costUsd: number;
  cached: boolean;
  clipsLeft: number;
  message: string;
}

/** What choosing a clip for the composition did. */
export interface ClipChoice {
  contentId: string;
  assetId: string;
  nextVersion: number;
  /** The step ids appended, compose first. */
  appended: string[];
  status: ContentStatus;
  feedback: string;
  message: string;
}

/**
 * **Re-generate with…**: the same prompt, on another model, as a new file.
 *
 * It spends: the call runs the operation `mc video generate` runs, under the
 * same toggles and the same caps, so a refusal here is the refusal a step
 * would have got and carries the provider's own sentence.
 */
export async function regenerateClip(
  contentId: string,
  assetId: string,
  model: string,
): Promise<ClipReroll> {
  return api<ClipReroll>(
    `/api/contents/${encodeURIComponent(contentId)}/clips/${encodeURIComponent(assetId)}/regenerate`,
    { method: 'POST', body: JSON.stringify({ model }) },
  );
}

/** **Use this clip**: a `compose` and a `render` step for the next version. */
export async function chooseClip(contentId: string, assetId: string): Promise<ClipChoice> {
  return api<ClipChoice>(
    `/api/contents/${encodeURIComponent(contentId)}/clips/${encodeURIComponent(assetId)}/choose`,
    { method: 'POST' },
  );
}

/* ------------------------------------- the video catalogue (US-084) */

/** One video model of OpenRouter's live catalogue, as the panel lists it. */
export interface VideoModelListing {
  model: string;
  name: string;
  /** Dollars a second at its cheapest sku, and at its dearest. */
  usdPerSecond: number | null;
  usdPerSecondMax: number | null;
  usdPerSecondBy: Record<string, number>;
  /** What it is metered in — a second of video, or something else entirely. */
  priceUnit: string;
  resolutions: string[];
  aspectRatios: string[];
  durations: number[];
  /** `null` means the catalogue does not say, which is not "no". */
  imageToVideo: boolean | null;
  audio: boolean | null;
}

/** The whole Video generation panel: the catalogue, and this install's own settings. */
export interface VideoCatalogueView {
  models: VideoModelListing[];
  at: string | null;
  source: 'live' | 'cache' | 'stale' | 'builtin';
  error: string | null;
  available: boolean;
  enabled: boolean;
  registered: boolean;
  model: string;
  resolution: string;
  maxClips: number;
  maxSecondsPerClip: number;
  defaultSeconds: number;
  unit: string;
  listed: VideoModelListing | null;
  usdPerSecond: number | null;
  ttlSeconds: number;
  warnings: string[];
  note: string;
  /** Image animation against the same catalogue (US-002). */
  animation: AnimationPlan;
  /** The talking head: its settings as they read now, and whether it can run. */
  talk: TalkPlan;
}

/** Mirrors the server's `AnimationPlan`: whether an image may be animated, and why not. */
/** Mirrors the server's `TalkPlan`. */
export interface TalkPlan {
  available: boolean;
  reason: string | null;
  enabled: boolean;
  model: string;
  listed: VideoModelListing | null;
  usdPerSecond: number | null;
  maxPerContent: number;
  maxSeconds: number;
  maxAttempts: number;
  warnings: string[];
}

export interface AnimationPlan {
  available: boolean;
  reason: string | null;
  enabled: boolean;
  model: string;
  listed: VideoModelListing | null;
  usdPerSecond: number | null;
  maxPerContent: number;
  maxSeconds: number;
  maxAttempts: number;
  warnings: string[];
}

/**
 * OpenRouter's video catalogue, as the server last read it.
 *
 * Cached on the server for an hour, so this is cheap to call on every visit to
 * Settings — and it never fails over a catalogue that could not be reached:
 * the answer says which list it is and why, and the panel prints that.
 */
export async function fetchVideoModels(signal?: AbortSignal): Promise<VideoCatalogueView> {
  return api<VideoCatalogueView>('/api/settings/video/models', signal ? { signal } : {});
}

/* -------------------------------------- format presets and tones (US-076) */

/** Which of the two rule tables a row belongs to. Mirrors the server's `RuleKind`. */
export type RuleKind = 'format' | 'tone';

/** One field a `rules/limits.json` `hard` bound cut back on the way in. */
export interface RuleClamp {
  field: string;
  asked: string;
  applied: string;
  bound: string;
}

/** A content that names a row, and so refuses its deletion. */
export interface RuleUser {
  id: string;
  name: string;
}

/** One format preset or tone, as the editor draws it. */
export interface RuleRowView {
  name: string;
  kind: RuleKind;
  /** The document in force: the built-in file, or the row saved over it. */
  document: unknown;
  /** The file's own version, or `null` for a row Settings invented. */
  builtIn: unknown;
  overridden: boolean;
  /** False for a row Settings invented: there is nothing to reset *to*. */
  resettable: boolean;
  usedBy: RuleUser[];
  summary: string;
}

/** Both tables, plus the bounds every row was clamped to. */
export interface RulesView {
  formats: RuleRowView[];
  tones: RuleRowView[];
  hard: Record<string, unknown>;
  precedence: string[];
  limitsFile: string;
}

/** A save answers with the whole view, so the panel redraws from the server. */
export interface RuleSaveResult {
  view: RulesView;
  clamps: RuleClamp[];
}

export async function fetchRules(signal?: AbortSignal): Promise<RulesView> {
  return api<RulesView>('/api/rules', signal ? { signal } : {});
}

/** The path segment: `/api/rules/formats/reel`, `/api/rules/tones/professional`. */
function ruleTable(kind: RuleKind): string {
  return kind === 'format' ? 'formats' : 'tones';
}

export async function saveRule(
  kind: RuleKind,
  name: string,
  document: unknown,
): Promise<RuleSaveResult> {
  return api<RuleSaveResult>(`/api/rules/${ruleTable(kind)}/${encodeURIComponent(name)}`, {
    method: 'PUT',
    body: JSON.stringify({ document }),
  });
}

/** **Reset to built-in**, or the delete that is the same gesture without a file. */
export async function resetRule(kind: RuleKind, name: string): Promise<RulesView> {
  const body = await api<{ view: RulesView }>(
    `/api/rules/${ruleTable(kind)}/${encodeURIComponent(name)}`,
    { method: 'DELETE' },
  );
  return body.view;
}

/* ----------------------------------------------- the shared library (US-023) */

/** Mirrors the server's `LIBRARY_ITEM_KINDS`. */
export const LIBRARY_KINDS = ['image', 'video', 'animation', 'render'] as const;

export type LibraryKind = (typeof LIBRARY_KINDS)[number];

/** How a kind reads on a badge, on the Library page and the content page alike. */
export const LIBRARY_KIND_LABEL: Record<LibraryKind, string> = {
  image: 'Image',
  video: 'Video',
  animation: 'Animation',
  render: 'Render',
};

/** Mirrors the server's `LIBRARY_ITEM_SOURCES`. */
export const LIBRARY_SOURCES = ['repository', 'generated', 'animation', 'render', 'upload'] as const;

export type LibrarySource = (typeof LIBRARY_SOURCES)[number];

/** Mirrors the server's `LibrarySearchHit`, plus the preview URL `GET /api/library` signs. */
export interface LibraryItem {
  id: string;
  title: string;
  description: string;
  tags: string[];
  kind: LibraryKind;
  source: LibrarySource;
  width: number | null;
  height: number | null;
  durationMs: number | null;
  /** The repository's name, or the clone name the item recorded once the row is gone. */
  repository: string | null;
  contentId: string | null;
  licence: string | null;
  attribution: string | null;
  score: number;
  createdAt: string;
  /** A presigned or public URL; `null` when no bucket is configured. */
  previewUrl: string | null;
}

export interface LibrarySearchResult {
  query: string;
  /** False when the scores are keyword-only, or the query was empty. */
  semantic: boolean;
  items: LibraryItem[];
}

export interface LibraryQuery {
  q?: string;
  kind?: LibraryKind;
  source?: LibrarySource;
  repositoryId?: string;
  limit?: number;
}

export async function searchLibrary(
  query: LibraryQuery,
  signal?: AbortSignal,
): Promise<LibrarySearchResult> {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return api<LibrarySearchResult>(`/api/library${search === '' ? '' : `?${search}`}`, { signal });
}

/**
 * One library item a content pulled in (US-028): a file whose asset row carries
 * `metadata.libraryItemId`. Mirrors the server's `ContentLibraryUse`.
 */
export interface ContentLibraryUse {
  assetId: string;
  /** Relative to `content/`: where the copy sits. */
  path: string;
  itemId: string;
  /** The item's title, or the file's name once the item is gone. */
  title: string;
  /** `null` once the item has been deleted from the library. */
  kind: LibraryKind | null;
  /** Whether the preview is a clip. */
  moving: boolean;
  /** A presigned or public URL; `null` with no bucket or no item. */
  previewUrl: string | null;
  removed: boolean;
}

export async function fetchContentLibraryUses(
  id: string,
  signal?: AbortSignal,
): Promise<ContentLibraryUse[]> {
  const answer = await api<{ items: ContentLibraryUse[] }>(
    `/api/contents/${encodeURIComponent(id)}/library-items`,
    { signal },
  );
  return answer.items;
}

/** Where the Library page opens one item's detail panel (US-028). */
export function libraryItemHref(id: string): string {
  return `/library?item=${encodeURIComponent(id)}`;
}

/**
 * Where an item stands in the search index (US-024): `updating` while the
 * re-embed an edit set off is still out, `unavailable` when no vector can be
 * made (OpenRouter off or no key) and the item is found on its words only.
 */
export type LibraryIndexState = 'current' | 'updating' | 'unavailable';

/** `GET` and `PATCH /api/library/:id`. */
export interface LibraryItemAnswer {
  item: LibraryItem;
  index: LibraryIndexState;
}

/** What the operator may correct; a field left out is left alone. */
export interface LibraryItemEdit {
  title?: string;
  description?: string;
  tags?: string[];
}

/** Mirrors the server's `LibraryItemDeletion`. */
export interface LibraryItemDeletion {
  deleted: boolean;
  key: string | null;
  keptCopy: boolean;
  objectDeleted: boolean;
}

/** Mirrors the server's `LIBRARY_DESCRIPTION_MIN_LENGTH`. */
export const LIBRARY_DESCRIPTION_MIN_LENGTH = 20;

export async function getLibraryItem(id: string, signal?: AbortSignal): Promise<LibraryItemAnswer> {
  return api<LibraryItemAnswer>(`/api/library/${encodeURIComponent(id)}`, { signal });
}

export async function updateLibraryItem(
  id: string,
  edit: LibraryItemEdit,
): Promise<LibraryItemAnswer> {
  return api<LibraryItemAnswer>(`/api/library/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(edit),
  });
}

export async function deleteLibraryItem(id: string): Promise<LibraryItemDeletion> {
  return api<LibraryItemDeletion>(`/api/library/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Mirrors the server's `LIBRARY_UPLOAD_TYPES`: what an upload may be, by extension. */
export const LIBRARY_UPLOAD_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.gif',
  '.svg',
  '.mp4',
  '.webm',
] as const;

/** Mirrors the server's `LIBRARY_UPLOAD_MAX_BYTES`. */
export const LIBRARY_UPLOAD_MAX_BYTES = 200 * 1024 * 1024;

/** The words the server's refusals use, so the page says the same thing before sending. */
export const LIBRARY_UPLOAD_LIMIT_TEXT =
  'The library takes PNG, JPEG, WebP, GIF, SVG, MP4 and WebM files up to 200 MB each.';

/** The words an upload carries besides its file. */
export interface LibraryUploadWords {
  title: string;
  description: string;
  tags: string[];
}

/** `POST /api/library`'s answer: the item, and whether its bytes were already in the library. */
export interface LibraryUploadAnswer extends LibraryItemAnswer {
  existed: boolean;
}

/** Why a file cannot be uploaded, checked before a byte is sent; `null` when it can. */
export function libraryUploadRefusal(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const extension = dot === -1 ? '' : file.name.slice(dot).toLowerCase();
  if (!(LIBRARY_UPLOAD_EXTENSIONS as readonly string[]).includes(extension)) {
    return `${file.name} is not a type the library takes. ${LIBRARY_UPLOAD_LIMIT_TEXT}`;
  }
  if (file.size > LIBRARY_UPLOAD_MAX_BYTES) {
    return `${file.name} is larger than 200 MB. ${LIBRARY_UPLOAD_LIMIT_TEXT}`;
  }
  if (file.size === 0) return `${file.name} is empty.`;
  return null;
}

/** The file as the request body; the words in the query string, which carries any character. */
export async function uploadLibraryFile(
  file: File,
  words: LibraryUploadWords,
): Promise<LibraryUploadAnswer> {
  const search = new URLSearchParams({
    filename: file.name,
    title: words.title,
    description: words.description,
    tags: words.tags.join(','),
  });
  return api<LibraryUploadAnswer>(`/api/library?${search.toString()}`, {
    method: 'POST',
    headers: { 'content-type': file.type === '' ? 'application/octet-stream' : file.type },
    body: file,
  });
}

/** `GET /api/library/describer`: whether **Suggest description** can be offered. */
export interface LibraryDescriberState {
  available: boolean;
  reason: string | null;
}

export async function getLibraryDescriber(signal?: AbortSignal): Promise<LibraryDescriberState> {
  return api<LibraryDescriberState>('/api/library/describer', { signal });
}

/** Words for a file the describer read (US-026), for the upload form to fill in. */
export async function suggestLibraryWords(file: File): Promise<LibraryUploadWords> {
  return api<LibraryUploadWords>(
    `/api/library/describe?${new URLSearchParams({ filename: file.name }).toString()}`,
    {
      method: 'POST',
      headers: { 'content-type': file.type === '' ? 'application/octet-stream' : file.type },
      body: file,
    },
  );
}

/* ---------------------------------------------------- idea batches (US-122) */

/** Where a batch stands: waiting, being worked, finished, or stopped. */
export type IdeaBatchStatus = 'queued' | 'working' | 'done' | 'failed';

/** Where one idea stands, from the line the operator typed to a decision. */
export type IdeaStatus = 'raw' | 'working' | 'drafted' | 'approved' | 'declined' | 'failed';

/** Mirrors the server's `IdeaBatchCounts`. */
export interface IdeaBatchCounts {
  total: number;
  drafted: number;
  approved: number;
  declined: number;
  failed: number;
  /** Not worked out yet: `raw` or `working`. */
  left: number;
}

/** Mirrors the server's `IdeaBatchView`: one batch as the list shows it. */
export interface IdeaBatchSummary {
  id: string;
  title: string;
  theme: string | null;
  requestedCount: number | null;
  /** Whether the ideas come from a pasted ideas document. */
  fromDocument: boolean;
  language: string | null;
  address: ContentAddress | null;
  /** The brand book the batch's contents are made in; `null` for none. */
  brandBookId: string | null;
  /** The campaign the batch's new ideas start in; `null` for none. */
  campaignId: string | null;
  status: IdeaBatchStatus;
  model: string | null;
  costUsd: number;
  error: string | null;
  /** While a working batch waits out Claude's usage limit: when it resumes. */
  heldUntil: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  counts: IdeaBatchCounts;
  primaryRepository: { id: string; name: string | null } | null;
}

/** Mirrors the server's `IdeaView`. */
export interface Idea {
  id: string;
  position: number;
  source: 'operator' | 'agent';
  /** The line typed, or for a document idea its whole section. */
  seed: string;
  /** The title a pasted table row or the ideas document gave it; `null` for a typed or suggested idea. */
  sourceTitle: string | null;
  status: IdeaStatus;
  title: string | null;
  kind: ContentKind | null;
  brief: string | null;
  tone: string | null;
  formats: string[] | null;
  creativeDirection: string | null;
  targetSeconds: number | null;
  captions: boolean | null;
  music: boolean | null;
  voiceover: boolean | null;
  watermark: boolean | null;
  rationale: string | null;
  costUsd: number;
  error: string | null;
  declineReason: string | null;
  /** What the content an approved idea becomes starts out with. */
  briefDetails: BriefDetails;
  contentId: string | null;
  content: { name: string; status: string } | null;
  /** Its batch's brand book, which the content it becomes is made in. */
  brandBookId: string | null;
  /** The campaign the idea belongs to on the board; `null` for none. */
  campaignId: string | null;
  /** Publish-by date, `YYYY-MM-DD`. */
  dueOn: string | null;
  /** Why the idea cannot move on; `null` when it is not blocked. */
  blockedReason: string | null;
  /** Manual order inside the Ideas column: lower is higher up. */
  boardRank: number;
  /** The hand a generated idea was built from (the ad bible US-019); `null` for an idea nobody dealt. */
  hand: IdeaHandCard[] | null;
  /** A dated moment's run window from the audience calendar; `null` for an undated idea. */
  runWindow: IdeaRunWindow | null;
  workedAt: string | null;
  decidedAt: string | null;
  createdAt: string;
}

/** One card of an idea's dealt hand. */
export interface IdeaHandCard {
  slot: string | null;
  deck: string;
  id: string;
  /** `null` when the active guidelines no longer have the card. */
  name: string | null;
}

/** When a dated idea's ad runs, `YYYY-MM-DD` both ends; `label` is the calendar date's own words. */
export interface IdeaRunWindow {
  from: string;
  to: string;
  label: string;
}

/** Mirrors the server's `IdeaBatchDetailView`: one batch with every idea in it. */
export interface IdeaBatch extends IdeaBatchSummary {
  repositories: { batchId: string; repositoryId: string; role: ContentRepositoryRole; name: string | null }[];
  ideas: Idea[];
}

/**
 * `POST /api/ideas`: typed ideas (`seeds`), a planning table, an ideas
 * document, or a theme and a count to suggest them from — exactly one.
 */
export interface IdeaBatchInput {
  title: string;
  repositories: { repositoryId: string; role: ContentRepositoryRole }[];
  language?: string | null;
  address?: ContentAddress | null;
  seeds?: string[];
  /** A pasted planning table, one idea per row; instead of `seeds`. */
  table?: string;
  /** A pasted ideas document, which Claude splits into its ideas; instead of `seeds`. */
  document?: string;
  theme?: string;
  requestedCount?: number;
  /** The campaign every new idea starts in; `null` or left out for none. */
  campaignId?: string | null;
}

/** One row of a pasted planning table, as the batch would take it. */
export interface PlanningRow {
  title: string;
  targetSeconds: number | null;
  briefDetails: BriefDetails;
  /** The publish-by date (`YYYY-MM-DD`) a date column gave. */
  dueOn: string | null;
  /** The campaign a campaign column named; `null` leaves the idea the batch's campaign. */
  campaign: { id: string; name: string } | null;
  /** A campaign cell that named no live campaign: kept as an extra detail instead. */
  unknownCampaign: string | null;
  /** The line the idea is drafted from. */
  seed: string;
}

/** The fields of a drafted idea the operator may change; `null` clears an optional one. */
export interface IdeaUpdate {
  title?: string;
  kind?: ContentKind;
  brief?: string;
  tone?: string;
  formats?: string[];
  creativeDirection?: string | null;
  targetSeconds?: number | null;
  captions?: boolean | null;
  music?: boolean | null;
  voiceover?: boolean | null;
  watermark?: boolean | null;
}

/** What approving answers: the idea, now `approved`, and the content made of it. */
export interface IdeaApproval {
  idea: Idea;
  contentId: string;
}

/** `/ideas/<id>`, the one place a batch is looked at. */
export function ideaBatchPath(id: string): string {
  return `/ideas/${encodeURIComponent(id)}`;
}

function ideaPath(batchId: string, ideaId: string): string {
  return `/api/ideas/${encodeURIComponent(batchId)}/ideas/${encodeURIComponent(ideaId)}`;
}

/** Every batch, newest first. */
export async function fetchIdeaBatches(signal?: AbortSignal): Promise<IdeaBatchSummary[]> {
  return api<IdeaBatchSummary[]>('/api/ideas', signal ? { signal } : {});
}

export async function fetchIdeaBatch(id: string, signal?: AbortSignal): Promise<IdeaBatch> {
  return api<IdeaBatch>(`/api/ideas/${encodeURIComponent(id)}`, signal ? { signal } : {});
}

export async function createIdeaBatch(input: IdeaBatchInput): Promise<IdeaBatchSummary> {
  return api<IdeaBatchSummary>('/api/ideas', { method: 'POST', body: JSON.stringify(input) });
}

/** What a pasted planning table would become, without creating anything; refused with the parser's message. */
export async function previewPlanningTable(table: string, signal?: AbortSignal): Promise<PlanningRow[]> {
  const preview = await api<{ rows: PlanningRow[] }>('/api/ideas/table-preview', {
    method: 'POST',
    body: JSON.stringify({ table }),
    ...(signal ? { signal } : {}),
  });
  return preview.rows;
}

/** Puts the ideas that could not be worked out back in line, and a failed batch with them. */
export async function retryIdeaBatch(id: string): Promise<IdeaBatchSummary> {
  return api<IdeaBatchSummary>(`/api/ideas/${encodeURIComponent(id)}/retry`, { method: 'POST' });
}

/** Removes a batch and its ideas; contents already made from them stay. */
export async function deleteIdeaBatch(id: string): Promise<void> {
  await api<void>(`/api/ideas/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function updateIdea(batchId: string, ideaId: string, update: IdeaUpdate): Promise<Idea> {
  return api<Idea>(ideaPath(batchId, ideaId), { method: 'PATCH', body: JSON.stringify(update) });
}

/**
 * Replaces all of an idea's brief details at once, while it is raw, drafted or
 * failed; approving it carries them to the content.
 */
export async function updateIdeaBriefDetails(batchId: string, ideaId: string, details: BriefDetails): Promise<Idea> {
  return api<Idea>(`${ideaPath(batchId, ideaId)}/brief-details`, { method: 'PUT', body: JSON.stringify(details) });
}

/**
 * Turns a drafted idea into a content; its planning starts once the
 * repositories are cloned. `autopilot` omitted is the Settings default.
 */
export async function approveIdea(
  batchId: string,
  ideaId: string,
  autopilot?: boolean,
): Promise<IdeaApproval> {
  return api<IdeaApproval>(`${ideaPath(batchId, ideaId)}/approve`, {
    method: 'POST',
    ...(autopilot === undefined ? {} : { body: JSON.stringify({ autopilot }) }),
  });
}

export async function declineIdea(
  batchId: string,
  ideaId: string,
  reason: string | null = null,
): Promise<Idea> {
  return api<Idea>(`${ideaPath(batchId, ideaId)}/decline`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

/** A declined idea back to waiting for a decision. */
export async function reopenIdea(batchId: string, ideaId: string): Promise<Idea> {
  return api<Idea>(`${ideaPath(batchId, ideaId)}/reopen`, { method: 'POST' });
}

/* ------------------------------------------------------------------ ad kit */

/** The four colours of a kit. Mirrors the server's `KIT_PALETTE_ROLES`. */
export const KIT_PALETTE_ROLES = ['brand', 'accent', 'ink', 'paper'] as const;
export type KitPaletteRole = (typeof KIT_PALETTE_ROLES)[number];
export const KIT_FONT_ROLES = ['display', 'body', 'mono'] as const;
export type KitFontRole = (typeof KIT_FONT_ROLES)[number];
export const KIT_TEXT_ROLES = ['headline', 'tag', 'body', 'caption'] as const;
export type KitTextRole = (typeof KIT_TEXT_ROLES)[number];
export const KIT_HEADLINE_CASES = ['none', 'uppercase', 'lowercase', 'capitalize'] as const;
export type KitHeadlineCase = (typeof KIT_HEADLINE_CASES)[number];
export type KitSource = 'built' | 'edited' | 'learned';

export interface KitFont {
  family: string;
  /** An https stylesheet or font URL, a relative font file, or `null` for a system font. */
  source: string | null;
}

export interface KitBandStyle {
  fill: KitPaletteRole | 'none';
  text: KitPaletteRole;
  radius: string;
}

/** A brand book's ad kit (one-shot ads US-002). Mirrors the server's `AdKit`. */
export interface AdKit {
  /** `roles`: what a colour means when the brand guide says (`{ accent: 'decision' }`, US-030). */
  palette: Record<KitPaletteRole, string> & { fieldRoles: KitPaletteRole[]; roles?: Partial<Record<KitPaletteRole, string>> };
  fonts: Record<KitFontRole, KitFont>;
  typography: Record<KitTextRole, { font: KitFontRole; weight: number }> & {
    headlineCase: KitHeadlineCase;
    headlineTracking: string;
  };
  logos: { lockup: Partial<Record<KitPaletteRole, string>>; mark: string };
  logoPlacement: 'end-card-only';
  endCard: {
    field: KitPaletteRole;
    lockup: string;
    ask: string;
    url: string;
    button: { fill: KitPaletteRole; text: KitPaletteRole; radius: string; weight: number };
    extras: never[];
  };
  captionBand: KitBandStyle;
  band: KitBandStyle;
  lowerThird: KitBandStyle;
  /** A drifting glow on the world layer; absent or false: grain, a texture or a blurred plate (R1). */
  glow?: boolean;
}

/** One saved version of a kit; the highest is the current kit. */
export interface KitVersion {
  id: string;
  brandBookId: string;
  version: number;
  kit: AdKit;
  source: KitSource;
  sourceContentId: string | null;
  note: string | null;
  createdAt: string;
}

/** The kit drawn: three whole HTML documents for sandboxed frames. */
export interface KitPreview {
  reel: string;
  portrait: string;
  endCard: string;
  /** Kit files the brand book does not have. */
  missingFiles: string[];
}

function kitUrl(brandBookId: string, rest = ''): string {
  return `/api/brand-books/${encodeURIComponent(brandBookId)}/kit${rest}`;
}

/** The current kit; a 404 `kit_not_found` means the book has none yet. */
export async function fetchKit(brandBookId: string, signal?: AbortSignal): Promise<KitVersion> {
  return api<KitVersion>(kitUrl(brandBookId), signal ? { signal } : {});
}

/** Every version, newest first. */
export async function fetchKitVersions(brandBookId: string, signal?: AbortSignal): Promise<KitVersion[]> {
  const body = await api<{ versions: KitVersion[] }>(kitUrl(brandBookId, '/versions'), signal ? { signal } : {});
  return body.versions;
}

/** Saves the whole kit as the next version. */
export async function saveKit(brandBookId: string, kit: AdKit, note: string | null): Promise<KitVersion> {
  return api<KitVersion>(kitUrl(brandBookId), { method: 'PUT', body: JSON.stringify({ kit, note }) });
}

/** Saves an earlier version again, as the next one. */
export async function restoreKitVersion(brandBookId: string, version: number): Promise<KitVersion> {
  return api<KitVersion>(kitUrl(brandBookId, `/restore/${String(version)}`), { method: 'POST' });
}

/** Draws a kit — saved or not — without saving it. */
export async function previewKit(brandBookId: string, kit: AdKit, signal?: AbortSignal): Promise<KitPreview> {
  return api<KitPreview>(kitUrl(brandBookId, '/preview'), {
    method: 'POST',
    body: JSON.stringify({ kit }),
    ...(signal ? { signal } : {}),
  });
}

/** What `POST /api/brand-books/:id/kit/build` answers: the saved version, or why there is none. */
export interface KitBuildResult {
  ok: boolean;
  message: string;
  version: KitVersion | null;
  missingFiles: string[];
}

/**
 * Builds the kit from the brand book in a container and saves it as the next
 * version. Slow by nature; a second build of the same book joins the first.
 */
export async function buildKit(brandBookId: string): Promise<KitBuildResult> {
  try {
    return await api<KitBuildResult>(kitUrl(brandBookId, '/build'), { method: 'POST', body: '{}' });
  } catch (cause) {
    // A failed build is a 502 with the same shape; its message is the answer.
    if (cause instanceof ApiError && cause.status === 502) {
      return { ok: false, message: cause.detail ?? cause.message, version: null, missingFiles: [] };
    }
    throw cause;
  }
}

/* -------------------------------------------------------------- house rules */

/** What a house rule is about, in the order a form offers them. Mirrors the server's `RULE_AREAS`. */
export const RULE_AREAS = ['look', 'end-card', 'logo', 'voice', 'sound', 'pace', 'words', 'staging', 'other'] as const;
export type RuleArea = (typeof RULE_AREAS)[number];

/** The longest rule the server takes. Mirrors the server's `MAX_RULE_LENGTH`. */
export const MAX_RULE_LENGTH = 500;

/** One house rule of a brand book (one-shot ads US-005). Mirrors the server's `BrandBookRule`. */
export interface BrandBookRule {
  id: string;
  brandBookId: string;
  rule: string;
  area: RuleArea;
  /** The content the correction was given on; `null` for a rule typed on the book. */
  sourceContentId: string | null;
  /** That content's version the correction was given on. */
  sourceVersion: number | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

function rulesUrl(brandBookId: string, ruleId?: string): string {
  const base = `/api/brand-books/${encodeURIComponent(brandBookId)}/rules`;
  return ruleId === undefined ? base : `${base}/${encodeURIComponent(ruleId)}`;
}

/** The book's live house rules, oldest first. */
export async function fetchBrandBookRules(brandBookId: string, signal?: AbortSignal): Promise<BrandBookRule[]> {
  const body = await api<{ rules: BrandBookRule[] }>(rulesUrl(brandBookId), signal ? { signal } : {});
  return body.rules;
}

/** Adds a rule typed on the book. */
export async function addBrandBookRule(brandBookId: string, rule: string, area: RuleArea): Promise<BrandBookRule> {
  return api<BrandBookRule>(rulesUrl(brandBookId), { method: 'POST', body: JSON.stringify({ rule, area }) });
}

/** Rewords a rule or moves it to another area; where it came from stays. */
export async function updateBrandBookRule(
  brandBookId: string,
  ruleId: string,
  change: { rule?: string; area?: RuleArea },
): Promise<BrandBookRule> {
  return api<BrandBookRule>(rulesUrl(brandBookId, ruleId), { method: 'PATCH', body: JSON.stringify(change) });
}

/** Deletes a rule: no next content starts with it. */
export async function deleteBrandBookRule(brandBookId: string, ruleId: string): Promise<void> {
  await api<void>(rulesUrl(brandBookId, ruleId), { method: 'DELETE' });
}

/* --------------------------------------------------------------- guidelines */

/** The keys of the bible's sections, in the order of `guidelines/README.md`. */
export const GUIDELINES_MARKDOWN_KEYS = [
  'charter',
  'message',
  'hooks',
  'proof',
  'psychology',
  'lines',
  'formats',
  'banned',
  'share_copy',
  'rubric',
  'style',
  'imagery',
  'examples',
] as const;
export const GUIDELINES_SECTION_KEYS = [...GUIDELINES_MARKDOWN_KEYS, 'decks', 'audiences'] as const;
export type GuidelinesSectionKey = (typeof GUIDELINES_SECTION_KEYS)[number];

/** A version's sections, as the server stores them; only the shapes the panel reads are typed. */
export type GuidelinesSections = Record<Exclude<(typeof GUIDELINES_MARKDOWN_KEYS)[number], 'banned'>, string> & {
  banned: { text: string; rules: unknown };
  decks: Record<string, unknown>;
  audiences: Record<string, { key: string; name: string; text: string } & Record<string, unknown>>;
};

export interface GuidelinesVersion {
  id: string;
  number: number;
  status: 'draft' | 'active' | 'retired';
  sections: GuidelinesSections;
  seedHashes: Record<string, string> | null;
  changeNote: string | null;
  parentId: string | null;
  approvedAt: string | null;
  createdAt: string;
}

/** A history row: a version without its sections. */
export interface GuidelinesVersionSummary {
  number: number;
  status: GuidelinesVersion['status'];
  changeNote: string | null;
  approvedAt: string | null;
  createdAt: string;
  parentNumber: number | null;
}

export interface GuidelinesOverview {
  active: GuidelinesVersion | null;
  draft: GuidelinesVersionSummary | null;
  history: GuidelinesVersionSummary[];
  /** Sections a newer build ships; `inDraft` once the draft has taken it. */
  pending: { key: GuidelinesSectionKey; inDraft: boolean }[];
  canUndo: boolean;
}

/** A banned rule (or one of its patterns) a write dropped. */
export interface GuidelinesBannedRemoval {
  rule: string;
  pattern?: string;
}

/** A draft write: the draft, or every reason the bible would not be sound. */
export type GuidelinesDraftResult =
  | { ok: true; draft: GuidelinesVersion; bannedRemovals: GuidelinesBannedRemoval[] }
  | { ok: false; message: string; problems: readonly string[] };

export interface GuidelinesSectionDiff {
  key: GuidelinesSectionKey;
  added: number;
  removed: number;
  lines: { op: 'same' | 'add' | 'remove'; text: string }[];
}

export interface GuidelinesDeckCard {
  id: string;
  name: string;
  weight: number;
  /** Brand book id → how often the card was dealt and how often it was made. */
  usage: Record<string, { dealt: number; made: number }>;
}

export interface GuidelinesDecksView {
  version: number | null;
  brandBooks: { id: string; name: string }[];
  decks: {
    deck: string;
    /** The audience whose bible holds it; `null` for a global deck. */
    audience: string | null;
    description: string | null;
    dealtTo: string[] | null;
    repeat: string | null;
    cards: GuidelinesDeckCard[];
  }[];
}

export async function fetchGuidelines(signal?: AbortSignal): Promise<GuidelinesOverview> {
  return api<GuidelinesOverview>('/api/guidelines', signal ? { signal } : {});
}

export async function fetchGuidelinesVersion(number: number, signal?: AbortSignal): Promise<GuidelinesVersion> {
  return api<GuidelinesVersion>(`/api/guidelines/versions/${String(number)}`, signal ? { signal } : {});
}

export async function fetchGuidelinesDraft(signal?: AbortSignal): Promise<GuidelinesVersion | null> {
  const body = await api<{ draft: GuidelinesVersion | null }>('/api/guidelines/draft', signal ? { signal } : {});
  return body.draft;
}

/** Answers the 400 of a refused draft as its problem list, so the editor can show them inline. */
async function guidelinesDraftWrite(path: string, init: RequestInit): Promise<GuidelinesDraftResult> {
  try {
    const body = await api<{ draft: GuidelinesVersion; bannedRemovals: GuidelinesBannedRemoval[] }>(path, init);
    return { ok: true, ...body };
  } catch (cause) {
    if (!(cause instanceof ApiError) || cause.status !== 400) throw cause;
    return { ok: false, message: cause.message, problems: cause.problems ?? [cause.message] };
  }
}

/** Writes sections over the draft (made from the active version when there is none). */
export async function saveGuidelinesDraft(
  sections: Partial<Record<GuidelinesSectionKey, unknown>>,
): Promise<GuidelinesDraftResult> {
  return guidelinesDraftWrite('/api/guidelines/draft', { method: 'PUT', body: JSON.stringify({ sections }) });
}

export async function activateGuidelinesDraft(note: string): Promise<GuidelinesVersion> {
  const body = await api<{ active: GuidelinesVersion }>('/api/guidelines/draft/activate', {
    method: 'POST',
    body: JSON.stringify({ note }),
  });
  return body.active;
}

export async function discardGuidelinesDraft(): Promise<void> {
  await api<void>('/api/guidelines/draft', { method: 'DELETE' });
}

export async function undoGuidelines(): Promise<GuidelinesVersion> {
  const body = await api<{ active: GuidelinesVersion }>('/api/guidelines/undo', { method: 'POST' });
  return body.active;
}

export async function fetchGuidelinesDiff(from: number, to: number): Promise<GuidelinesSectionDiff[]> {
  const body = await api<{ sections: GuidelinesSectionDiff[] }>(
    `/api/guidelines/diff?from=${String(from)}&to=${String(to)}`,
  );
  return body.sections;
}

/** Takes the newer shipped seed of one section into the draft. */
export async function takeGuidelinesSeed(key: GuidelinesSectionKey): Promise<GuidelinesDraftResult> {
  return guidelinesDraftWrite(`/api/guidelines/seed/${key}`, { method: 'POST' });
}

export async function fetchGuidelinesDecks(signal?: AbortSignal): Promise<GuidelinesDecksView> {
  return api<GuidelinesDecksView>('/api/guidelines/decks', signal ? { signal } : {});
}
